// frontend/cms/src/components/OperationDetailDialog.tsx
// ✅ コンポーネント分割済み: CmsGpsPinMap / CmsActivityEditModal / CmsActivityAddModal /
//    operationDetailShared(型・判定ヘルパー) は別ファイルに分離

import { User, Truck, MapPin, Package, Clock, Navigation, CheckCircle, AlertCircle, TrendingUp, Coffee, Fuel, Play, Square, ClipboardCheck, ChevronDown, ChevronUp, XCircle, Pencil } from 'lucide-react';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { apiClient } from '../utils/api';
import Button from './common/Button';
import Modal from './common/Modal';
import CmsActivityEditModal from './CmsActivityEditModal';
import CmsActivityAddModal from './CmsActivityAddModal';
import { OperationDetail, OperationActivity, GpsRecord, InspectionRecord, TimelineEvent, OperationDebugTimelineEvent, InspectionItemDetail, OperationDetailDialogProps, CmsEditEvent, isBreakGroupEvt } from './operationDetailShared';

const OperationDetailDialog: React.FC<OperationDetailDialogProps> = ({
  operationId,
  isOpen,
  onClose,
  initialOperation
}) => {
  console.log('[OperationDetailDialog] Rendering:', { operationId, isOpen });

  // ===================================================================
  // State管理
  // ===================================================================
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // データ state
  const [operation, setOperation] = useState<OperationDetail | null>(null);
  const [activities, setActivities] = useState<OperationActivity[]>([]);
  const [gpsRecords, setGpsRecords] = useState<GpsRecord[]>([]);
  const [inspections, setInspections] = useState<InspectionRecord[]>([]);
  
  // @ts-ignore - 将来使用する可能性があるため保持
  const [inspectionsLoading, setInspectionsLoading] = useState(false);
  // @ts-ignore - 将来使用する可能性があるため保持
  const [inspectionsError, setInspectionsError] = useState<string | null>(null);

  // ✅ OperationDebug統合タイムライン用State
  const [operationDebugTimelineEvents, setOperationDebugTimelineEvents] = useState<OperationDebugTimelineEvent[]>([]);
  const [inspectionItemDetails, setInspectionItemDetails] = useState<InspectionItemDetail[]>([]);

  // ✅ 走行軌跡用GPSログ state（イベントPINとは別）
  const [routeGpsLogs, setRouteGpsLogs] = useState<Array<{
    latitude: number;
    longitude: number;
    recordedAt: string;
    speedKmh: number | null;
  }>>([]);
  // ✅ DB実記録件数（間引き前の総件数）
  const [totalGpsCount, setTotalGpsCount] = useState<number>(0);
  // ✅ Phase2: 運行区間距離（GPS実測/Routes API推定）
  const [routeSegments, setRouteSegments] = useState<Array<{
    segmentIndex: number;
    fromActivityType: string;
    toActivityType: string;
    distanceSource: string;
    distanceKm: number;
    routePolyline: string | null;
  }>>([]);

  // ✅ タイムラインイベントからGPSポイントを抽出（地図表示用）
  const timelineGpsPoints = useMemo(() => {
    return operationDebugTimelineEvents
      .filter(event => event.gpsLocation != null)
      .map(event => ({
        latitude: event.gpsLocation!.latitude,
        longitude: event.gpsLocation!.longitude,
        recordedAt: event.gpsLocation!.recordedAt,
        eventType: event.eventType,
        sequenceNumber: event.sequenceNumber,
        notes: event.notes || ''
      }));
  }, [operationDebugTimelineEvents]);

  // タブ切り替え state
  const [activeTab, setActiveTab] = useState<'basic' | 'timeline' | 'gps' | 'inspection'>('basic');

  // ✅ UI制御用State
  const [showOperationTimeline, setShowOperationTimeline] = useState(true);
  const [showInspectionDetails, setShowInspectionDetails] = useState(true);

  // ✅ イベント編集モーダル
  const [editEvent, setEditEvent] = useState<CmsEditEvent | null>(null);
  const [editItems, setEditItems] = useState<{ id: string; name: string }[]>([]);
  const [editCustomers, setEditCustomers] = useState<{ id: string; name: string }[]>([]);
  // 🆕 イベント追加モーダル（記録漏れの後追い登録用）
  // ✅ イベント追加: mobileと同じ「＋」マーカーによる挿入位置指定機能
  const [insertMode, setInsertMode] = useState(false);
  const [pickerContext, setPickerContext] = useState<{ afterSeq: number; prevEndTime: string | null; lastLoadingCustomerId?: string; lastLoadingCustomerName?: string } | null>(null);

  // ✅ Google Maps用State
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // ===================================================================
  // Google Maps初期化
  // ===================================================================

  /**
   * ✅ Google Mapsスクリプト読み込み
   */
  useEffect(() => {
    console.log('🌍 [Maps Loading Debug] === Google Maps loading useEffect START ===');
    console.log('🌍 [Maps Loading Debug] isOpen:', isOpen);
    console.log('🌍 [Maps Loading Debug] activeTab:', activeTab);
    
    const loadGoogleMaps = () => {
      console.log('🌍 [Maps Loading Debug] loadGoogleMaps function called');
      
      if (window.google && window.google.maps) {
        console.log('✅ [Maps Loading Debug] Google Maps already loaded');
        setMapsLoaded(true);
        return;
      }

      const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
      console.log('🔑 [Maps Loading Debug] API Key exists?', !!GOOGLE_MAPS_API_KEY);
      console.log('🔑 [Maps Loading Debug] API Key length:', GOOGLE_MAPS_API_KEY.length);

      if (!GOOGLE_MAPS_API_KEY || GOOGLE_MAPS_API_KEY === 'YOUR_GOOGLE_MAPS_API_KEY_HERE') {
        console.error('❌ [Maps Loading Debug] Invalid or missing API key');
        setMapError('Google Maps APIキーが設定されていません');
        return;
      }

      const existingScript = document.getElementById('google-maps-script');
      if (existingScript) {
        console.log('✅ [Maps Loading Debug] Google Maps script already exists');
        if ((window as any).google?.maps) {
          console.log('✅ [Maps Loading Debug] Already loaded, setting mapsLoaded=true');
          setMapsLoaded(true);
        } else {
          existingScript.addEventListener('load', () => {
            console.log('✅ [Maps Loading Debug] Existing script loaded');
            setMapsLoaded(true);
          });
        }
        return;
      }

      console.log('📥 [Maps Loading Debug] Creating new Google Maps script tag...');
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=marker,places,geometry&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('✅ [Maps Loading Debug] Google Maps script loaded successfully');
        setMapsLoaded(true);
      };
      script.onerror = () => {
        console.error('❌ [Maps Loading Debug] Google Maps script loading failed');
        setMapError('Google Mapsの読み込みに失敗しました');
      };
      document.head.appendChild(script);
      console.log('📥 [Maps Loading Debug] Script tag appended to document.head');
    };

    // ✅ 修正②③【根本原因】: 以前は activeTab === 'gps' の場合のみ読み込んでいたため、
    // GPSタブを開かずにイベント追加/編集モーダル（場所ピン調整・新規地点登録マップ）を
    // 使おうとすると mapsLoaded が永遠に false のままになり、
    // 「地図を読み込み中...」から進めなくなっていた。ダイアログが開いている間は
    // タブに関係なく読み込む。
    if (isOpen) {
      console.log('✅ [Maps Loading Debug] Conditions met - calling loadGoogleMaps()');
      loadGoogleMaps();
    } else {
      console.log('⚠️ [Maps Loading Debug] Conditions not met - skipping');
    }
    
    console.log('🌍 [Maps Loading Debug] === Google Maps loading useEffect END ===');
  }, [isOpen, activeTab]);

  /**
   * ✅ Google Map初期化とGPSルート描画
   * ✅ 修正: routeGpsLogs を localStorage 設定に依存せず常時描画
   */
  useEffect(() => {
    console.log('🗺️ [Map Debug] === Map initialization useEffect START ===');
    console.log('🗺️ [Map Debug] Conditions check:');
    console.log('  - mapsLoaded:', mapsLoaded);
    console.log('  - mapRef.current:', !!mapRef.current);
    console.log('  - gpsRecords.length:', gpsRecords.length);
    console.log('  - routeGpsLogs.length:', routeGpsLogs.length);
    console.log('  - activeTab:', activeTab);
    console.log('  - activeTab === "gps":', activeTab === 'gps');
    
    // ✅ timelineGpsPointsを優先、なければgpsRecordsにフォールバック
    const activeGpsPoints = timelineGpsPoints.length > 0
      ? timelineGpsPoints
      : gpsRecords.map(r => ({
          latitude: r.latitude,
          longitude: r.longitude,
          recordedAt: r.recordedAt,
          eventType: 'GPS_LOG' as const,
          sequenceNumber: 0,
          notes: ''
        }));

    // routeGpsLogsがある場合はactiveGpsPointsが0でも地図を初期化する
    const hasAnyGpsData = activeGpsPoints.length > 0 || routeGpsLogs.length > 0;

    if (!mapsLoaded || !mapRef.current || !hasAnyGpsData || activeTab !== 'gps') {
      console.warn('⚠️ [Map Debug] Map initialization skipped - conditions not met');
      console.warn('  - mapsLoaded:', mapsLoaded);
      console.warn('  - mapRef.current:', !!mapRef.current);
      console.warn('  - hasAnyGpsData:', hasAnyGpsData);
      console.warn('  - activeTab === "gps":', activeTab === 'gps');
      return;
    }

    console.log('✅ [Map Debug] All conditions met - initializing map...');

    try {
      // ✅ イベントタイプ→日本語ラベルのマッピング
      const getEventLabel = (eventType: string): { short: string; full: string; color: string } => {
        // ★ Trade Colors準拠・全eventType対応・?廃止
        const labels: Record<string, { short: string; full: string; color: string }> = {
          TRIP_START:          { short: '始', full: '運行開始',   color: '#10B981' },
          TRIP_END:            { short: '終', full: '運行終了',   color: '#F44336' },
          PRE_INSPECTION:      { short: '前', full: '運行前点検', color: '#6366F1' },
          POST_INSPECTION:     { short: '後', full: '運行後点検', color: '#8B5CF6' },
          LOADING:             { short: '積', full: '積込',       color: '#2196F3' },
          LOADING_START:       { short: '積', full: '積込開始',   color: '#2196F3' },
          LOADING_COMPLETE:    { short: '積', full: '積込完了',   color: '#1565C0' },
          LOADING_ARRIVED:     { short: '積', full: '積込到着',   color: '#2196F3' },
          LOADING_COMPLETED:   { short: '積', full: '積込完了',   color: '#1565C0' },
          UNLOADING:           { short: '降', full: '荷降',       color: '#4CAF50' },
          UNLOADING_START:     { short: '降', full: '荷降開始',   color: '#4CAF50' },
          UNLOADING_COMPLETE:  { short: '降', full: '荷降完了',   color: '#2E7D32' },
          UNLOADING_ARRIVED:   { short: '降', full: '荷降到着',   color: '#4CAF50' },
          UNLOADING_COMPLETED: { short: '降', full: '荷降完了',   color: '#2E7D32' },
          BREAK_START:         { short: '休', full: '休憩・待機開始',   color: '#9C27B0' },
          BREAK_END:           { short: '休', full: '休憩・待機終了',   color: '#6A1B9A' },
          BREAK:               { short: '休', full: '休憩・待機',       color: '#9C27B0' },
          FUELING:             { short: '油', full: '給油',       color: '#FF9800' },
          REFUELING:           { short: '油', full: '給油',       color: '#FF9800' },
          FUEL:                { short: '油', full: '給油',       color: '#FF9800' },
          TRANSPORTING:        { short: '運', full: '輸送中',     color: '#2196F3' },
          WAITING:             { short: '待', full: '待機中',     color: '#9C27B0' },
          WAITING_START:       { short: '待', full: '待機開始',   color: '#9C27B0' },
          WAITING_END:         { short: '待', full: '待機終了',   color: '#6A1B9A' },
          CARGO_WORK_START:    { short: '役', full: '荷役開始',   color: '#1565C0' },
          CARGO_WORK_END:      { short: '役', full: '荷役終了',   color: '#0D47A1' },
          GPS_LOG:             { short: '●', full: 'GPS記録',    color: '#3B82F6' },
        };
        return labels[eventType] ?? { short: '●', full: eventType, color: '#94A3B8' };
      };

      // ✅ 地図の中心座標を計算
      let centerLat = 34.6937;  // 大阪デフォルト
      let centerLng = 135.5023;

      if (routeGpsLogs.length > 0) {
        const firstLog = routeGpsLogs[0];
        if (firstLog) { centerLat = firstLog.latitude; centerLng = firstLog.longitude; }
      } else if (activeGpsPoints.length > 0) {
        const firstPt = activeGpsPoints[0];
        if (firstPt) { centerLat = firstPt.latitude; centerLng = firstPt.longitude; }
      }

      // ✅ Google Map インスタンス初期化
      // mapId は AdvancedMarkerElement に必須。環境変数 → サンプルID の順でフォールバック
      const resolvedMapId = (import.meta as any).env?.VITE_GOOGLE_MAP_ID || '90f87356969d889c';
      const map = new google.maps.Map(mapRef.current!, {
        center: { lat: centerLat, lng: centerLng },
        zoom: 13,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapId: resolvedMapId,
      });
      mapInstanceRef.current = map;

      if (routeGpsLogs.length > 0) {
        // ★ GPS欠落区間（5分以上のギャップ）を点線で表示
        const GAP_THRESHOLD_MS = 5 * 60 * 1000;
        let segStart = 0;
        for (let i = 1; i <= routeGpsLogs.length; i++) {
          const isLastSeg = i === routeGpsLogs.length;
          const prevLog = routeGpsLogs[i - 1];
          const currLog = routeGpsLogs[i];
          const isGap = !isLastSeg && prevLog && currLog &&
            (new Date(currLog.recordedAt).getTime() - new Date(prevLog.recordedAt).getTime()) > GAP_THRESHOLD_MS;

          if (isGap || isLastSeg) {
            // 通常区間: 実線（青）
            const segPath = routeGpsLogs.slice(segStart, i).map(log => ({ lat: log.latitude, lng: log.longitude }));
            if (segPath.length >= 2) {
              new google.maps.Polyline({
                path: segPath,
                geodesic: true,
                strokeColor: '#3B82F6',
                strokeOpacity: 0.85,
                strokeWeight: 3,
                map: map,
              });
            }
            if (isGap && prevLog && currLog) {
              // 欠落区間: 点線（グレー）
              new google.maps.Polyline({
                path: [
                  { lat: prevLog.latitude, lng: prevLog.longitude },
                  { lat: currLog.latitude, lng: currLog.longitude },
                ],
                geodesic: true,
                strokeColor: '#64748B',
                strokeOpacity: 0,
                strokeWeight: 2,
                icons: [{
                  icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.7, strokeColor: '#64748B', scale: 3 },
                  offset: '0',
                  repeat: '12px',
                }],
                map: map,
              });
              segStart = i;
            }
          }
        }

        // ✅ Phase2: 推定経路（Google Routes API）のポリライン重ね描画
        console.log('[RouteSegments][Frontend] 🖌️ 描画チェック: routeSegments件数=' + routeSegments.length + ', geometry libロード済み=' + !!(window as any).google?.maps?.geometry?.encoding);
        if (routeSegments.length > 0) {
          console.log('[RouteSegments][Frontend] 🖌️ routeSegments詳細:', routeSegments.map(s => ({ idx: s.segmentIndex, from: s.fromActivityType, to: s.toActivityType, source: s.distanceSource, hasPolyline: !!s.routePolyline })));
        }
        if ((window as any).google?.maps?.geometry?.encoding && routeSegments.length > 0) {
          const orangeCandidates = routeSegments.filter(seg => seg.routePolyline && (seg.distanceSource === 'GPS_PARTIAL_ESTIMATE' || seg.distanceSource === 'FULL_ESTIMATE'));
          console.log(`[RouteSegments][Frontend] 🖌️ オレンジ線描画対象: ${orangeCandidates.length}件 / 全${routeSegments.length}件`);
          const estimateInfoWindow = new google.maps.InfoWindow();
          routeSegments
            .filter(seg => seg.routePolyline && (seg.distanceSource === 'GPS_PARTIAL_ESTIMATE' || seg.distanceSource === 'FULL_ESTIMATE'))
            .forEach(seg => {
              try {
                const decodedPath = (window as any).google.maps.geometry.encoding.decodePath(seg.routePolyline as string);
                const estimatePolyline = new google.maps.Polyline({
                  path: decodedPath,
                  geodesic: true,
                  strokeColor: '#F97316',
                  strokeOpacity: 0,
                  strokeWeight: 3,
                  icons: [{
                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, strokeColor: '#F97316', scale: 3 },
                    offset: '0',
                    repeat: '10px',
                  }],
                  map: map,
                });
                const sourceLabel = seg.distanceSource === 'GPS_PARTIAL_ESTIMATE'
                  ? '一部推定（記録済みGPS地点を経由するGoogle推奨ルート）'
                  : '全推定（Google推奨ルート）';
                estimatePolyline.addListener('click', (e: any) => {
                  estimateInfoWindow.setContent(
                    `<div style="font-size:12px;line-height:1.6;">🧭 推定経路<br>区間: ${seg.fromActivityType} → ${seg.toActivityType}<br>距離: ${Number(seg.distanceKm).toFixed(2)} km<br>根拠: ${sourceLabel}</div>`
                  );
                  estimateInfoWindow.setPosition(e.latLng);
                  estimateInfoWindow.open(map);
                });
              } catch (decodeErr) {
                console.warn('⚠️ [Map Debug] 推定経路ポリラインのデコード失敗', decodeErr);
              }
            });
        }

        // GPS記録ポイントのインフォウィンドウ（個別マーカーはrouteGpsLogs数が多いため省略）
        console.log('✅ [Map Debug] 走行軌跡描画完了:', routeGpsLogs.length, '点');
      } else {
        console.log('ℹ️ [Map Debug] routeGpsLogs なし - 走行軌跡描画スキップ');
      }

      // =====================================================================
      // ✅ Step2: イベントPINポイントのポリライン（activeGpsPoints）
      // =====================================================================
      if (activeGpsPoints.length > 0) {
        const path = activeGpsPoints.map(point => ({
          lat: point.latitude,
          lng: point.longitude
        }));

        console.log('📍 [Map Debug] Event path created with', path.length, 'points');

        // イベント間をつなぐポリライン（細い緑色の点線）
        new google.maps.Polyline({
          path: path,
          geodesic: true,
          strokeColor: '#10B981',  // emerald-500
          strokeOpacity: 0.6,
          strokeWeight: 2,
          map: map
        });
        console.log('✅ [Map Debug] Event polyline drawn');

        // ✅ イベントごとのマーカーを描画
        const infoWindow = new google.maps.InfoWindow();

        activeGpsPoints.forEach((point, index) => {
          const label = getEventLabel(point.eventType);
          const isFirst = index === 0;
          const isLast = index === activeGpsPoints.length - 1;

          // BUG-011: AdvancedMarkerElement 移行
          const evPinEl = document.createElement('div');
          const markerSize = isFirst || isLast ? 28 : 22;
          const fontSize = isFirst || isLast ? 11 : 9;
          evPinEl.style.cssText = [
            `width:${markerSize}px`,
            `height:${markerSize}px`,
            `border-radius:50%`,
            `background:${label.color}`,
            `border:2.5px solid #fff`,
            `display:flex`,
            `align-items:center`,
            `justify-content:center`,
            `color:#fff`,
            `font-size:${fontSize}px`,
            `font-weight:bold`,
            `cursor:pointer`,
            `box-shadow:0 2px 6px rgba(0,0,0,.45)`,
            `font-family:'Noto Sans JP',sans-serif`,
            `line-height:1`,
            `user-select:none`,
          ].join(';');
          evPinEl.textContent = label.short;
          // AdvancedMarkerElement（mapId必須）→ フォールバック: 旧Marker
          let marker: any;
          if ((google.maps as any).marker?.AdvancedMarkerElement) {
            marker = new (google.maps as any).marker.AdvancedMarkerElement({
              position: { lat: point.latitude, lng: point.longitude },
              map: map,
              title: `${point.sequenceNumber > 0 ? point.sequenceNumber + '. ' : ''}${label.full}`,
              content: evPinEl,
            });
          } else {
            // フォールバック: 旧 google.maps.Marker（mapId不要）
            marker = new google.maps.Marker({
              position: { lat: point.latitude, lng: point.longitude },
              map: map,
              title: `${point.sequenceNumber > 0 ? point.sequenceNumber + '. ' : ''}${label.full}`,
              icon: {
                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                  `<svg xmlns="http://www.w3.org/2000/svg" width="${markerSize*2}" height="${markerSize*2}">` +
                  `<circle cx="${markerSize}" cy="${markerSize}" r="${markerSize-2}" fill="${label.color}" stroke="white" stroke-width="2.5"/>` +
                  `<text x="${markerSize}" y="${markerSize + fontSize/3}" text-anchor="middle" fill="white" font-size="${fontSize}" font-weight="bold" font-family="sans-serif">${label.short}</text>` +
                  `</svg>`
                )}`,
                scaledSize: new google.maps.Size(markerSize*2, markerSize*2),
                anchor: new google.maps.Point(markerSize, markerSize),
              },
            });
          }

          // クリックで情報ウィンドウ表示（AdvancedMarker/旧Marker両対応）
          const markerTarget = (google.maps as any).marker?.AdvancedMarkerElement
            ? marker
            : marker;
          markerTarget.addListener('click', () => {
            const content = `
              <div style="padding:8px;min-width:160px;font-family:sans-serif;font-size:12px;">
                <div style="font-weight:bold;font-size:13px;margin-bottom:4px;color:#1f2937;">
                  ${point.sequenceNumber > 0 ? point.sequenceNumber + '. ' : ''}${label.full}
                </div>
                <div style="color:#6b7280;margin-bottom:2px;">
                  📍 ${point.latitude.toFixed(6)}, ${point.longitude.toFixed(6)}
                </div>
                <div style="color:#6b7280;margin-bottom:2px;">
                  🕐 ${new Date(point.recordedAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                </div>
                ${point.notes ? `<div style="color:#374151;margin-top:4px;border-top:1px solid #e5e7eb;padding-top:4px;">${point.notes}</div>` : ''}
              </div>
            `;
            infoWindow.setContent(content);
            infoWindow.open({ map, anchor: marker });
          });
        });

        console.log('✅ [Map Debug] Event markers drawn:', activeGpsPoints.length, '件');
      }

      // =====================================================================
      // ✅ Step3: 地図の表示範囲を全ポイントが収まるように調整
      // =====================================================================
      const allPoints: google.maps.LatLngLiteral[] = [];
      if (routeGpsLogs.length > 0) {
        routeGpsLogs.forEach(p => allPoints.push({ lat: p.latitude, lng: p.longitude }));
      }
      if (activeGpsPoints.length > 0) {
        activeGpsPoints.forEach(p => allPoints.push({ lat: p.latitude, lng: p.longitude }));
      }

      if (allPoints.length > 1) {
        const bounds = new google.maps.LatLngBounds();
        allPoints.forEach(p => bounds.extend(p));
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        console.log('✅ [Map Debug] Map bounds fitted to', allPoints.length, 'points');
      }

      console.log('✅ [Map Debug] === Google Map initialization SUCCESS ===');
      console.log('✅ [Map Debug] routeGpsLogs:', routeGpsLogs.length, '件');
      console.log('✅ [Map Debug] Event GPS points:', activeGpsPoints.length, '件');
      console.log('✅ [Map Debug] Map center:', { lat: centerLat, lng: centerLng });
    } catch (err) {
      console.error('❌ [Map Debug] === Google Map initialization FAILED ===');
      console.error('❌ [Map Debug] Error:', err);
      console.error('❌ [Map Debug] Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      setMapError('地図の表示中にエラーが発生しました');
    }
  }, [mapsLoaded, gpsRecords, timelineGpsPoints, routeGpsLogs, activeTab, mapRef]);

  // ===================================================================
  // データ取得
  // ===================================================================
  
  /**
   * 運行基本情報を取得
   */
  const fetchOperationDetail = async () => {
    console.log('📋 [Operation Debug] === fetchOperationDetail START ===');
    console.log('📋 [Operation Debug] operationId:', operationId);
    
    try {
      const response = await apiClient.get(`/operations/${operationId}`);
      
      console.log('📡 [Operation Debug] API Response:', response);
      console.log('📡 [Operation Debug] response.success:', response.success);
      console.log('📡 [Operation Debug] response.data:', response.data);
      
      if (response.success && response.data) {
        const responseData: any = response.data;
        let operationData: OperationDetail;
        
        console.log('🔍 [Operation Debug] Parsing response data...');
        console.log('🔍 [Operation Debug] responseData.data?.data exists?', !!responseData.data?.data);
        console.log('🔍 [Operation Debug] responseData.data exists?', !!responseData.data);
        
        // データ構造に応じて柔軟に対応
        if (responseData.data?.data) {
          operationData = responseData.data.data as OperationDetail;
          console.log('✅ [Operation Debug] Using responseData.data.data');
        } else if (responseData.data) {
          operationData = responseData.data as OperationDetail;
          console.log('✅ [Operation Debug] Using responseData.data');
        } else {
          operationData = responseData as OperationDetail;
          console.log('✅ [Operation Debug] Using responseData directly');
        }
        
        console.log('📋 [Operation Debug] Extracted operation data:', {
          id: operationData.id,
          vehicleId: operationData.vehicleId,
          driverId: operationData.driverId,
          status: operationData.status,
          hasVehicles: !!operationData.vehicles,
          vehiclesId: operationData.vehicles?.id
        });
        
        // ✅ 修正: vehicles/usersOperationsDriverIdTousers が含まれない場合の補完
        // timeline API レスポンスの operation フィールドに含まれていない場合、
        // フラットなレスポンスから vehicle/driver 情報を取り出す
        const raw: any = operationData;
        if (!raw.vehicles && !raw.usersOperationsDriverIdTousers) {
          // 一覧から渡された selectedRecord のデータをフォールバックとして補完
          // (OperationRecords が selectedRecord を props として渡している)
        }
        // vehicles/driverが取得できなかった場合、initialOperation（一覧データ）で補完
        if (initialOperation && operationData) {
          const op: any = operationData;
          if (!op.vehicles && initialOperation.vehicles) {
            op.vehicles = initialOperation.vehicles;
          }
          if (!op.usersOperationsDriverIdTousers && initialOperation.usersOperationsDriverIdTousers) {
            op.usersOperationsDriverIdTousers = initialOperation.usersOperationsDriverIdTousers;
          }
        }
        setOperation(operationData);
        console.log('✅ [Operation Debug] Operation state updated', {
          hasVehicles: !!(operationData as any).vehicles,
          hasDriver: !!(operationData as any).usersOperationsDriverIdTousers,
          plateNumber: (operationData as any).vehicles?.plateNumber,
          driverName: (operationData as any).usersOperationsDriverIdTousers?.name
        });
      } else {
        console.error('❌ [Operation Debug] Response not successful or no data');
        setError('運行記録の取得に失敗しました');
      }
    } catch (err) {
      console.error('❌ [Operation Debug] Error fetching operation:', err);
      console.error('❌ [Operation Debug] Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      setError('運行記録の取得中にエラーが発生しました');
    }
    
    console.log('📋 [Operation Debug] === fetchOperationDetail END ===');
  };

  /**
   * 運行詳細（積込・積下）を取得
   */
  const fetchOperationActivities = async () => {
    try {
      console.log('[OperationDetailDialog] Fetching operation activities:', operationId);
      const response = await apiClient.get('/operation-details', {
        params: {
          operationId: operationId,
          page: 1,
          limit: 100
        }
      });
      
      console.log('[OperationDetailDialog] Activities response:', response);
      
      if (response.success && response.data) {
        // データ構造に応じて柔軟に対応
        let activitiesData: OperationActivity[] = [];
        const data: any = response.data;
        
        if (data.data?.data && Array.isArray(data.data.data)) {
          activitiesData = data.data.data;
        } else if (data.data && Array.isArray(data.data)) {
          activitiesData = data.data;
        } else if (Array.isArray(data)) {
          activitiesData = data;
        }
        
        // シーケンス番号でソート
        activitiesData.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
        
        setActivities(activitiesData);
        console.log('[OperationDetailDialog] Activities loaded:', activitiesData.length);
      }
    } catch (err) {
      console.error('[OperationDetailDialog] Error fetching activities:', err);
      // エラーは致命的ではないので、空配列のまま継続
    }
  };

  /**
   * GPS記録を取得（フォールバック用）
   */
  const fetchGpsRecords = async () => {
    console.log('🗺️ [GPS Debug] === fetchGpsRecords START ===');
    console.log('🗺️ [GPS Debug] operationId:', operationId);
    console.log('🗺️ [GPS Debug] operation:', operation);
    
    try {
      // 運行情報からvehicleIdと期間を取得
      if (!operation) {
        console.warn('⚠️ [GPS Debug] Operation data not loaded yet - ABORTING');
        return;
      }

      const vehicleId = operation.vehicleId || operation.vehicles?.id;
      const startDate = operation.actualStartTime || operation.plannedStartTime;
      const endDate = operation.actualEndTime || new Date().toISOString();

      console.log('🗺️ [GPS Debug] Extracted params:', { 
        vehicleId, 
        startDate, 
        endDate,
        hasVehicles: !!operation.vehicles,
        vehiclesId: operation.vehicles?.id
      });

      if (!vehicleId) {
        console.error('❌ [GPS Debug] Vehicle ID not found - ABORTING');
        return;
      }

      console.log('✅ [GPS Debug] Calling API /gps/tracks with params:', { 
        vehicleIds: vehicleId, 
        startDate, 
        endDate, 
        simplify: false 
      });

      // ✅ 正しいエンドポイント: /gps/tracks
      const response = await apiClient.get('/gps/tracks', {
        params: {
          vehicleIds: vehicleId,
          startDate: startDate,
          endDate: endDate,
          simplify: false
        }
      });
      
      console.log('📡 [GPS Debug] API Response:', response);
      console.log('📡 [GPS Debug] response.success:', response.success);
      console.log('📡 [GPS Debug] response.data type:', typeof response.data);
      console.log('📡 [GPS Debug] response.data:', response.data);
      
      if (response.success && response.data) {
        let gpsData: GpsRecord[] = [];
        // BUG-013対応: 二重ネスト解消
        // response.data が { success, data: Array } の場合は data.data を使う
        const rawData: any = response.data;
        const data: any = Array.isArray(rawData)
          ? rawData
          : Array.isArray(rawData?.data)
          ? rawData.data
          : rawData;
        
        console.log('🔍 [GPS Debug] Processing response data...');
        console.log('🔍 [GPS Debug] rawData:', rawData);
        console.log('🔍 [GPS Debug] Is data array?', Array.isArray(data));
        
        // レスポンス構造の解析
        if (Array.isArray(data)) {
          console.log('📊 [GPS Debug] Data is array, length:', data.length);
          console.log('📊 [GPS Debug] First element:', data[0]);
          
          // tracks配列から最初の車両のtrackを取得
          const vehicleTrack = data.find((t: any) => t.vehicleId === vehicleId);
          
          console.log('🚗 [GPS Debug] Found vehicleTrack:', vehicleTrack);
          console.log('🚗 [GPS Debug] vehicleTrack.track exists?', !!vehicleTrack?.track);
          console.log('🚗 [GPS Debug] vehicleTrack.track length:', vehicleTrack?.track?.length);
          
          if (vehicleTrack && Array.isArray(vehicleTrack.track)) {
            console.log('✅ [GPS Debug] Processing track points...');
            gpsData = vehicleTrack.track.map((point: any, index: number) => {
              if (index < 3) {  // 最初の3ポイントのみログ
                console.log(`📍 [GPS Debug] Point ${index}:`, point);
              }
              return {
                id: `gps-${index}`,
                latitude: point.latitude,
                longitude: point.longitude,
                recordedAt: point.timestamp,
                speedKmh: point.speed || 0,
                altitude: point.altitude,
                accuracyMeters: point.accuracy,
                heading: point.heading
              };
            });
          } else {
            console.warn('⚠️ [GPS Debug] No vehicleTrack or track array found');
          }
        } else {
          console.warn('⚠️ [GPS Debug] Response data is not an array');
        }
        
        // 時刻でソート
        gpsData.sort((a, b) => 
          new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
        );
        
        console.log('✅ [GPS Debug] Final gpsData length:', gpsData.length);
        console.log('✅ [GPS Debug] First GPS record:', gpsData[0]);
        console.log('✅ [GPS Debug] Last GPS record:', gpsData[gpsData.length - 1]);
        
        setGpsRecords(gpsData);
        console.log('✅ [GPS Debug] GPS records state updated');
      } else {
        console.warn('⚠️ [GPS Debug] Response not successful or no data');
      }
    } catch (err) {
      console.error('❌ [GPS Debug] Error fetching GPS records:', err);
      console.error('❌ [GPS Debug] Error stack:', err instanceof Error ? err.stack : 'No stack trace');
    }
    
    console.log('🗺️ [GPS Debug] === fetchGpsRecords END ===');
  };

  /**
   * 点検記録を取得
   */
  const fetchInspections = async () => {
    console.log('🔍 [Debug] fetchInspections開始', { operationId });
    
    try {
      setInspectionsLoading(true);
      
      console.log('🔍 [Debug] operationId使用', { operationId });
      
      if (!operationId) {
        console.warn('⚠️ [Debug] operationIdがnull/undefined');
        setInspectionsError('運行情報が見つかりません');
        return;
      }

      const response: any = await apiClient.get('/inspections', {
        params: { 
          operationId: operationId,
          page: 1, 
          limit: 100 
        }
      });
      
      console.log('✅ [Debug] 点検記録API応答', {
        status: response?.status,
        hasData: !!response?.data,
        dataType: typeof response?.data,
        dataKeys: response?.data ? Object.keys(response.data) : []
      });

      // レスポンス処理
      const responseData: any = response.data;
      let inspectionsData: InspectionRecord[];
      
      if (responseData.data?.data) {
        inspectionsData = responseData.data.data as InspectionRecord[];
      } else if (responseData.data) {
        inspectionsData = responseData.data as InspectionRecord[];
      } else {
        inspectionsData = responseData as InspectionRecord[];
      }

      console.log('✅ [Debug] 点検記録データ解析完了', {
        inspectionsCount: inspectionsData.length,
        inspections: inspectionsData
      });

      setInspections(inspectionsData);
      setInspectionsError(null);

      // ✅ FIX No.27: 点検項目タブの項目別結果を取得（従来は取得処理が無く常に0件）
      try {
        const itemsRes: any = await apiClient.get(`/inspections/operation/${operationId}/items`);
        const raw: any = itemsRes?.data;
        const list: any[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
        setInspectionItemDetails(list as InspectionItemDetail[]);
      } catch (itemErr) {
        console.warn('点検項目詳細の取得に失敗', itemErr);
        setInspectionItemDetails([]);
      }

    } catch (error: any) {
      console.error('❌ [Debug] 点検記録取得エラー', {
        error: error?.message,
        response: error?.response?.data
      });
      setInspectionsError('点検記録の取得に失敗しました');
    } finally {
      setInspectionsLoading(false);
    }
  };

  /**
   * ✅ 統合タイムライン取得（OperationDebugから完全移植）
   */
  const fetchIntegratedTimeline = async (opId: string) => {
    try {
      console.log('[OperationDetailDialog] Fetching integrated timeline:', opId);
      
      const response = await apiClient.get('/operation-details', {
        params: {
          operationId: opId,
          page: 1,
          limit: 100
        }
      });
      
      console.log('[OperationDetailDialog] Timeline response:', response);
      
      if (response.success && response.data) {
        let eventsData: OperationDebugTimelineEvent[] = [];
        let operationData: OperationDetail | null = null;
        
        // ✅ 3層ネスト対応（response.data.data.data）
        const outerData: any = response.data;
        const innerData: any = outerData.data || outerData;
        
        // イベントデータ抽出（複数パターン対応）
        if (innerData.data && Array.isArray(innerData.data)) {
          eventsData = innerData.data;
          console.log('[OperationDetailDialog] ✅ Pattern 1: innerData.data (3-level nesting)');
        } else if (Array.isArray(innerData)) {
          eventsData = innerData;
          console.log('[OperationDetailDialog] ✅ Pattern 2: innerData is array');
        } else if (outerData.data && Array.isArray(outerData.data)) {
          eventsData = outerData.data;
          console.log('[OperationDetailDialog] ✅ Pattern 3: outerData.data');
        } else if (Array.isArray(outerData)) {
          eventsData = outerData;
          console.log('[OperationDetailDialog] ✅ Pattern 4: outerData is array');
        }
        
        // 運行情報抽出
        if (innerData.operation) {
          operationData = innerData.operation;
        } else if (outerData.operation) {
          operationData = outerData.operation;
        }
        
        console.log('[OperationDetailDialog] 📊 Extracted data:', {
          eventsCount: eventsData.length,
          eventTypes: eventsData.length > 0 ? Array.from(new Set(eventsData.map(e => e.eventType))) : [],
          hasOperation: !!operationData
        });
        
        setOperationDebugTimelineEvents(eventsData);
        // ✅ 修正【根本原因確定】: 以前は `!operation` というクロージャ経由の古い変数を
        // 参照しており、fetchOperationDetail()が先にvehicleId等を含む完全な運行情報を
        // setOperation済みであっても、この判定は常に古い(null)ままのoperationを見てしまい、
        // タイムラインAPIのoperationフィールド（vehicleId/driverIdを含まない簡略版）で
        // 無条件に上書きしてしまっていた。setState の関数形式を使い、Reactが管理する
        // 常に最新のstateを見て判定することで、既存の完全な運行情報を上書きしないようにする。
        if (operationData) {
          setOperation(prev => prev ? prev : operationData);
        }

        // ✅ routeGpsLogs を抽出してstateにセット
        const routeLogs = innerData.routeGpsLogs || outerData.routeGpsLogs || [];
        setRouteGpsLogs(routeLogs);
        // ✅ totalGpsCount: DB実記録件数（間引き前）
        const rawTotal = innerData.totalGpsCount ?? outerData.totalGpsCount ?? routeLogs.length;
        setTotalGpsCount(rawTotal);
        console.log('[OperationDetailDialog] 📡 routeGpsLogs:', routeLogs.length, '件 / DB総件数:', rawTotal, '件');
      }
    } catch (err) {
      console.error('[OperationDetailDialog] Error fetching timeline:', err);
    }
  };

  /**
   * ✅ 点検項目詳細取得（no-op: /debug/operations エンドポイントは存在しないため）
   */
  const fetchInspectionItemDetails = async (opId: string) => {
    console.log('[OperationDetailDialog] fetchInspectionItemDetails called (no-op):', opId);
    // この関数は何もしません（/debug/operations エンドポイントが存在しないため）
  };

  /**
   * 全データを取得
   * ✅ 修正: GPS記録はoperation情報取得後に実行
   */
  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // ✅ Step 1: 運行基本情報を先に取得
      await fetchOperationDetail();
      
      // ✅ Step 2: 並行して他のデータを取得（items も取得して編集モーダルで使用）
      await Promise.all([
        fetchOperationActivities(),
        fetchIntegratedTimeline(operationId),
        fetchInspectionItemDetails(operationId),
        (async () => {
          console.log('[RouteSegments][Frontend] 🌐 GET /route-segments/' + operationId + ' 開始');
          try {
            const res = await apiClient.get(`/route-segments/${operationId}`);
            const d: any = res;
            console.log('[RouteSegments][Frontend] 📡 レスポンス生データ:', d);
            const arr = d?.data?.data ?? d?.data ?? [];
            const list = Array.isArray(arr) ? arr : [];
            const sourceCounts = list.reduce((acc: Record<string, number>, seg: any) => {
              acc[seg.distanceSource] = (acc[seg.distanceSource] || 0) + 1;
              return acc;
            }, {});
            console.log(`[RouteSegments][Frontend] ✅ 区間データ取得完了: ${list.length}件, 内訳=`, sourceCounts);
            setRouteSegments(list);
            // 🆕 FIX: GPSタブを開く前に前もってgeometryライブラリを読み込んでおく
            if (list.length > 0 && (window as any).google?.maps?.importLibrary) {
              (window as any).google.maps.importLibrary('geometry').catch((e: any) => {
                console.warn('[RouteSegments][Frontend] ⚠️ geometryライブラリの先読みに失敗:', e);
              });
            }
          } catch (segErr) {
            console.error('[RouteSegments][Frontend] ❌ 区間距離データ取得失敗:', segErr);
          }
        })(),
        (async () => {
          try {
            const res = await apiClient.get('/items', { params: { page: 1, limit: 100 } });
            const d: any = res;
            const arr = d?.data?.data?.data ?? d?.data?.data ?? d?.data ?? [];
            setEditItems(Array.isArray(arr) ? arr : []);
          } catch { /* items 取得失敗は致命的ではない */ }
        })(),
        (async () => {
          try {
            const res = await apiClient.get('/customers', { params: { page: 1, limit: 200 } });
            const d: any = res;
            const arr = d?.data?.data?.customers ?? d?.data?.customers ?? d?.data?.data?.data ?? d?.data?.data ?? d?.data ?? [];
            setEditCustomers(Array.isArray(arr) ? arr : []);
          } catch { /* customers 取得失敗は致命的ではない */ }
        })(),
      ]);
      
    } catch (err) {
      console.error('[OperationDetailDialog] Error fetching data:', err);
      setError('データの取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // ===================================================================
  // Effects
  // ===================================================================
  
  useEffect(() => {
    if (isOpen && operationId) {
      // ✅ 追加: operationId変更時に全stateをリセット（古いデータの残留防止）
      setOperation(null);
      setActivities([]);
      setGpsRecords([]);
      setInspections([]);
      setOperationDebugTimelineEvents([]);
      setRouteGpsLogs([]);
      setTotalGpsCount(0);
      setRouteSegments([]);
      setError(null);
      setActiveTab('basic');  // タブも基本情報に戻す
      // Google Mapsインスタンスをクリア（次の運行で再初期化させる）
      mapInstanceRef.current = null;

      console.log('[OperationDetailDialog] Dialog opened, fetching data');
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, operationId]);

  // タイムラインGPSポイントがない場合のみGPS生ログをフォールバック取得
  useEffect(() => {
    if (operation && isOpen && activeTab === 'gps' && timelineGpsPoints.length === 0 && gpsRecords.length === 0) {
      console.log('🔄 [GPS Auto-fetch] No timeline GPS points, fetching raw GPS records as fallback...');
      fetchGpsRecords();
    }
  }, [operation, isOpen, activeTab, timelineGpsPoints.length]);

  // 運行情報取得後に点検記録を取得
  useEffect(() => {
    if (operation) {
      fetchInspections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation]);

  // ===================================================================
  // タイムライン統合ヘルパー関数
  // ===================================================================

  /**
   * 運行詳細と点検記録を統合したタイムラインイベントを生成
   */
  // @ts-ignore - 将来使用する可能性があるため保持
  const getTimelineEvents = (): TimelineEvent[] => {
    const events: TimelineEvent[] = [];
    
    console.log('🔍 [Timeline] タイムラインイベント生成開始', {
      activitiesCount: activities.length,
      inspectionsCount: inspections.length
    });
    
    // 運行詳細（積込・積下等）をイベントに追加
    activities.forEach(activity => {
      const timestamp = activity.actualStartTime || activity.plannedTime;
      if (timestamp) {
        events.push({
          id: `activity-${activity.id}`,
          type: 'activity',
          timestamp: new Date(timestamp),
          sequenceNumber: activity.sequenceNumber,
          data: activity
        });
      }
    });
    
    // 点検記録をイベントに追加
    inspections.forEach(inspection => {
      const timestamp = inspection.startedAt;
      if (timestamp) {
        events.push({
          id: `inspection-${inspection.id}`,
          type: 'inspection',
          timestamp: new Date(timestamp),
          data: inspection
        });
      }
    });
    
    // 時系列順にソート
    events.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    
    console.log('✅ [Timeline] タイムラインイベント生成完了', {
      totalEvents: events.length,
      activityEvents: events.filter(e => e.type === 'activity').length,
      inspectionEvents: events.filter(e => e.type === 'inspection').length
    });
    
    return events;
  };

  // ===================================================================
  // ヘルパー関数 - ✅ OperationDebugと完全統一
  // ===================================================================
  
  /**
   * ステータスバッジを取得
   */
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      COMPLETED: { label: '完了', className: 'bg-green-100 text-green-800' },
      IN_PROGRESS: { label: '運行中', className: 'bg-blue-100 text-blue-800' },
      CANCELLED: { label: 'キャンセル', className: 'bg-red-100 text-red-800' },
      PLANNING: { label: '計画中', className: 'bg-yellow-100 text-yellow-800' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.PLANNING;
    return (
      <span className={`px-3 py-1 text-sm font-semibold rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  /**
   * ✅ 作業種別の情報取得 - OperationDebugと完全統一（Lucideアイコン使用）
   */
  // @ts-ignore - 将来使用する可能性があるため保持
  const getActivityTypeInfo = (activityType: string) => {
    const typeConfig: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
      LOADING: { label: '積込', icon: <Truck className="w-5 h-5" />, className: 'bg-indigo-100 text-indigo-800' },
      UNLOADING: { label: '荷降', icon: <Truck className="w-5 h-5" />, className: 'bg-purple-100 text-purple-800' },
      FUELING: { label: '給油', icon: <Fuel className="w-5 h-5" />, className: 'bg-orange-100 text-orange-800' },
      REFUELING: { label: '給油', icon: <Fuel className="w-5 h-5" />, className: 'bg-orange-100 text-orange-800' },
      BREAK: { label: '休憩・待機', icon: <Coffee className="w-5 h-5" />, className: 'bg-purple-100 text-purple-800' },
      BREAK_START: { label: '休憩・待機', icon: <Coffee className="w-5 h-5" />, className: 'bg-purple-100 text-purple-800' },
      BREAK_END: { label: '休憩・待機終了', icon: <Coffee className="w-5 h-5" />, className: 'bg-purple-100 text-purple-800' },
      MAINTENANCE: { label: 'メンテナンス', icon: <AlertCircle className="w-5 h-5" />, className: 'bg-red-100 text-red-800' },
      TRANSPORTING: { label: '運搬中', icon: <Navigation className="w-5 h-5" />, className: 'bg-cyan-100 text-cyan-800' },
      WAITING: { label: '待機', icon: <Clock className="w-5 h-5" />, className: 'bg-gray-100 text-gray-800' },
      WAITING_START: { label: '待機開始', icon: <Clock className="w-5 h-5" />, className: 'bg-gray-100 text-gray-800' },
      WAITING_END: { label: '待機終了', icon: <Clock className="w-5 h-5" />, className: 'bg-gray-100 text-gray-800' },
      CARGO_WORK_START: { label: '荷役開始', icon: <Truck className="w-5 h-5" />, className: 'bg-indigo-100 text-indigo-800' },
      CARGO_WORK_END: { label: '荷役終了', icon: <Truck className="w-5 h-5" />, className: 'bg-indigo-100 text-indigo-800' },
      TRIP_START: { label: '運行開始', icon: <Play className="w-5 h-5" />, className: 'bg-green-100 text-green-800' },
      TRIP_END: { label: '運行終了', icon: <Square className="w-5 h-5" />, className: 'bg-red-100 text-red-800' },
      PRE_INSPECTION: { label: '運行前点検', icon: <ClipboardCheck className="w-5 h-5" />, className: 'bg-blue-100 text-blue-800' },
      POST_INSPECTION: { label: '運行後点検', icon: <ClipboardCheck className="w-5 h-5" />, className: 'bg-emerald-100 text-emerald-800' }
    };

    return typeConfig[activityType] || {
      label: activityType,
      icon: <MapPin className="w-5 h-5" />,
      className: 'bg-gray-100 text-gray-800'
    };
  };

  /**
   * ✅ イベントタイプの情報取得（OperationDebugから完全移植）
   */
  const getEventTypeInfo = (eventType: string) => {
    const typeConfig: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
      TRIP_START: { label: '運行開始', icon: <Play className="w-5 h-5" />, className: 'bg-green-100 text-green-800' },
      TRIP_END: { label: '運行終了', icon: <Square className="w-5 h-5" />, className: 'bg-red-100 text-red-800' },
      PRE_INSPECTION: { label: '運行前点検', icon: <ClipboardCheck className="w-5 h-5" />, className: 'bg-blue-100 text-blue-800' },
      POST_INSPECTION: { label: '運行後点検', icon: <ClipboardCheck className="w-5 h-5" />, className: 'bg-emerald-100 text-emerald-800' },
      LOADING: { label: '積込', icon: <Truck className="w-5 h-5" />, className: 'bg-indigo-100 text-indigo-800' },
      UNLOADING: { label: '荷降', icon: <Truck className="w-5 h-5" />, className: 'bg-purple-100 text-purple-800' },
      FUELING: { label: '給油', icon: <Fuel className="w-5 h-5" />, className: 'bg-orange-100 text-orange-800' },
      REFUELING: { label: '給油', icon: <Fuel className="w-5 h-5" />, className: 'bg-orange-100 text-orange-800' },
      BREAK: { label: '休憩・待機', icon: <Coffee className="w-5 h-5" />, className: 'bg-purple-100 text-purple-800' },
      BREAK_START: { label: '休憩・待機', icon: <Coffee className="w-5 h-5" />, className: 'bg-purple-100 text-purple-800' },
      BREAK_END: { label: '休憩・待機終了', icon: <Coffee className="w-5 h-5" />, className: 'bg-purple-100 text-purple-800' },
      MAINTENANCE: { label: 'メンテナンス', icon: <AlertCircle className="w-5 h-5" />, className: 'bg-red-100 text-red-800' },
      TRANSPORTING: { label: '運搬中', icon: <Navigation className="w-5 h-5" />, className: 'bg-cyan-100 text-cyan-800' },
      WAITING: { label: '待機', icon: <Clock className="w-5 h-5" />, className: 'bg-gray-100 text-gray-800' },
      WAITING_START: { label: '待機開始', icon: <Clock className="w-5 h-5" />, className: 'bg-gray-100 text-gray-800' },
      WAITING_END: { label: '待機終了', icon: <Clock className="w-5 h-5" />, className: 'bg-gray-100 text-gray-800' },
      CARGO_WORK_START: { label: '荷役開始', icon: <Truck className="w-5 h-5" />, className: 'bg-indigo-100 text-indigo-800' },
      CARGO_WORK_END: { label: '荷役終了', icon: <Truck className="w-5 h-5" />, className: 'bg-indigo-100 text-indigo-800' },
      LOADING_ARRIVED:    { label: '積込場所 到着', icon: <MapPin />, className: 'bg-blue-100 text-blue-800' },
      LOADING_COMPLETED:  { label: '積込完了',     icon: <CheckCircle />, className: 'bg-indigo-100 text-indigo-800' },
      UNLOADING_ARRIVED:  { label: '荷降場所 到着', icon: <MapPin />, className: 'bg-orange-100 text-orange-800' },
      UNLOADING_COMPLETED:{ label: '荷降完了',     icon: <CheckCircle />, className: 'bg-purple-100 text-purple-800' },
      // REQ-020: imageUrl は eventType ではないがフィールドとして表示
    };

    return typeConfig[eventType] || {
      label: eventType,
      icon: <MapPin className="w-5 h-5" />,
      className: 'bg-gray-100 text-gray-800'
    };
  };

  /**
   * ✅ ヘルパー関数（OperationDebugから完全移植）
   */
  const getPassedIcon = (isPassed: boolean | null) => {
    if (isPassed === null || isPassed === undefined) {
      return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
    return isPassed ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <XCircle className="w-4 h-4 text-red-500" />
    );
  };

  const getInspectionTypeBadge = (type: string) => {
    const typeConfig: Record<string, { color: string; text: string }> = {
      PRE_OPERATION: { color: 'bg-blue-100 text-blue-800', text: '運行前点検' },
      POST_OPERATION: { color: 'bg-emerald-100 text-emerald-800', text: '運行後点検' },
      PRE_TRIP: { color: 'bg-blue-100 text-blue-800', text: '運行前点検' },
      POST_TRIP: { color: 'bg-emerald-100 text-emerald-800', text: '運行後点検' },
      PERIODIC: { color: 'bg-yellow-100 text-yellow-800', text: '定期点検' },
    };

    const config = typeConfig[type] || { color: 'bg-gray-100 text-gray-800', text: type };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  /**
   * 点検結果のバッジを取得
   */
  // @ts-ignore - 将来使用する可能性があるため保持
  const getInspectionResultBadge = (result: string) => {
    const resultConfig = {
      PASS: { label: '合格', className: 'bg-green-100 text-green-800' },
      FAIL: { label: '不合格', className: 'bg-red-100 text-red-800' },
      WARNING: { label: '警告', className: 'bg-yellow-100 text-yellow-800' }
    };

    const config = resultConfig[result as keyof typeof resultConfig] || resultConfig.WARNING;
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  /**
   * 点検種別の情報取得 - OperationDebugと完全統一（Lucideアイコン使用）
   */
  // @ts-ignore - 将来使用する可能性があるため保持
  const getInspectionTypeInfo = (inspectionType: string) => {
    const typeConfig: Record<string, { label: string; icon: React.ReactNode; className: string; description: string }> = {
      PRE_TRIP: { 
        label: '運行前点検', 
        icon: <ClipboardCheck className="w-5 h-5" />, 
        className: 'bg-blue-100 text-blue-800',
        description: '運行開始前の車両点検'
      },
      POST_TRIP: { 
        label: '運行後点検', 
        icon: <ClipboardCheck className="w-5 h-5" />, 
        className: 'bg-emerald-100 text-emerald-800',
        description: '運行終了後の車両点検'
      }
    };

    return typeConfig[inspectionType] || {
      label: inspectionType,
      icon: <CheckCircle className="w-5 h-5" />,
      className: 'bg-gray-100 text-gray-800',
      description: '点検'
    };
  };

  /**
   * ✅ 時刻フォーマット - OperationDebugと統一
   */
  const formatTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  /**
   * ✅ GPS座標フォーマット - OperationDebugと統一
   */
  const formatGps = (lat: number, lng: number) => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  // ===================================================================
  // レンダリング
  // ===================================================================
  
  if (!isOpen) return null;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="運行記録詳細"
      size="xl"
    >
      <div className="space-y-6">
        {/* ローディング表示 */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">データを読み込み中...</p>
            </div>
          </div>
        )}

        {/* エラー表示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {/* データ表示 */}
        {!loading && !error && operation && (
          <>
            {/* タブナビゲーション */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('basic')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'basic'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    基本情報
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('timeline')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'timeline'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    運行タイムライン
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('gps')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'gps'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Navigation className="w-4 h-4" />
                    GPSルート
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('inspection')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'inspection'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    点検項目
                  </div>
                </button>
              </nav>
            </div>

            {/* タブコンテンツ */}
            <div className="mt-6">
              {/* 基本情報タブ */}
              {activeTab === 'basic' && (
                <div className="space-y-6">
                  {/* 基本情報セクション */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Truck className="w-5 h-5 text-gray-600" />
                      基本情報
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">運行番号</p>
                        <p className="font-medium text-lg">{operation.operationNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">ステータス</p>
                        {getStatusBadge(operation.status)}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">運転手</p>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <p className="font-medium">
                            {operation.usersOperationsDriverIdTousers?.name || '-'}
                          </p>
                        </div>
                      </div>
                      {(operation as any)?.customer?.name && (
                        <div>
                          <p className="text-sm text-gray-500 mb-1">客先</p>
                          <p className="font-medium text-green-700">🏢 {(operation as any).customer.name}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-500 mb-1">車両</p>
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-gray-400" />
                          <p className="font-medium">
                            {operation.vehicles?.plateNumber || '-'}
                            {operation.vehicles?.model && ` (${operation.vehicles.model})`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 運行情報セクション */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-gray-600" />
                      運行情報
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">出発時刻</p>
                        <p className="font-medium">
                          {operation.actualStartTime
                            ? new Date(operation.actualStartTime).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })
                            : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">到着時刻</p>
                        <p className="font-medium">
                          {operation.actualEndTime
                            ? new Date(operation.actualEndTime).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })
                            : '-'}
                        </p>
                      </div>
                      {/* 予定開始/終了時刻は不要のため削除 */}
                      <div>
                        <p className="text-sm text-gray-500 mb-1">総走行距離</p>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-gray-400" />
                          <p className="font-medium">
                            {operation.totalDistanceKm ? `${operation.totalDistanceKm} km` : '-'}
                          </p>
                        </div>
                        {(operation as any).routeSegmentDistanceKm != null && (
                          <p className="text-xs text-gray-400 mt-1">
                            GPS/Routes推定: {Number((operation as any).routeSegmentDistanceKm).toFixed(1)} km
                            （{(operation as any).routeSegmentDistanceSource === 'ACTUAL' ? '実測'
                              : (operation as any).routeSegmentDistanceSource === 'MIXED' ? '一部推定'
                              : '推定'}）
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">燃料消費</p>
                        <p className="font-medium">
                          {operation.fuelConsumedLiters ? `${operation.fuelConsumedLiters} L` : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* 備考 */}
                  {operation.notes && (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold mb-2">備考</h3>
                      <p className="text-gray-700">{operation.notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ✅ 運行タイムラインタブ - OperationDebugと完全統一 */}
              {activeTab === 'timeline' && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-5 h-5 text-gray-600" />
                      <h2 className="text-lg font-semibold text-gray-900">運行タイムライン（統合版）</h2>
                      <span className="text-sm text-gray-500">({operationDebugTimelineEvents.length}件)</span>
                      <button
                        type="button"
                        onClick={() => { setInsertMode(v => !v); setPickerContext(null); }}
                        className={`ml-2 flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border ${insertMode ? 'text-white bg-blue-600 border-blue-600' : 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100'}`}
                      >
                        {insertMode ? '追加をやめる' : '＋ イベント追加'}
                      </button>
                    </div>
                    <button
                      onClick={() => setShowOperationTimeline(!showOperationTimeline)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                    >
                      {showOperationTimeline ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {showOperationTimeline && operationDebugTimelineEvents.length > 0 && (
                    <div className="space-y-3">
                      {/* ─────────────────────────────────────────────
                          1日の運搬サマリカード
                      ───────────────────────────────────────────── */}
                      {(() => {
                        const _evs = operationDebugTimelineEvents;
                        // 積込・積降回数（ARRIVED = 到着した回数）
                        const loadingCount   = _evs.filter(e => e.eventType === 'LOADING_ARRIVED').length;
                        const unloadingCount = _evs.filter(e => e.eventType === 'UNLOADING_ARRIVED').length;
                        // 積込完了イベントから重量・品目を集計
                        const completedLoadings = _evs.filter(e => e.eventType === 'LOADING_COMPLETED');
                        // ✅ 修正②【根本原因】: 複数品目選択時は detailItems 配列に全品目が入るが、
                        // 従来はここが単一の items フィールドしか見ておらず、2つ目以降の品目が
                        // 運搬品目タグ・品目別台数・客先別集計のすべてから欠落していた。
                        const itemNamesOf = (e: any): string[] =>
                          (e.detailItems && e.detailItems.length > 0)
                            ? e.detailItems.map((di: any) => di.itemName).filter((n: any) => !!n)
                            : (e.items ? [e.items.name] : []);
                        const uniqueItems = [...new Set(
                          completedLoadings.flatMap(e => itemNamesOf(e))
                        )];
                        const totalWeight = completedLoadings.reduce((sum, e) => sum + (e.quantityTons || 0), 0);
                        // 走行距離は operation state から取得
                        const distanceKm = operation?.totalDistanceKm ?? null;
                        return (
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                            <div className="text-sm font-bold text-blue-800 mb-3">
                              📊 1日の運搬サマリ
                            </div>
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                              {/* 積込回数 */}
                              <div className="bg-white rounded-lg p-3 border border-blue-100 text-center shadow-sm">
                                <div className="text-2xl font-bold text-indigo-600">{loadingCount}</div>
                                <div className="text-xs text-gray-500 mt-0.5">積込回数</div>
                              </div>
                              {/* 積降回数 */}
                              <div className="bg-white rounded-lg p-3 border border-blue-100 text-center shadow-sm">
                                <div className="text-2xl font-bold text-purple-600">{unloadingCount}</div>
                                <div className="text-xs text-gray-500 mt-0.5">積降回数</div>
                              </div>
                              {/* 総重量 */}
                              <div className="bg-white rounded-lg p-3 border border-blue-100 text-center shadow-sm">
                                <div className="text-2xl font-bold text-green-600">
                                  {totalWeight > 0 ? totalWeight.toFixed(1) : '-'}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">総重量 (t)</div>
                              </div>
                              {/* 走行距離 */}
                              <div className="bg-white rounded-lg p-3 border border-blue-100 text-center shadow-sm">
                                <div className="text-2xl font-bold text-orange-600">
                                  {distanceKm != null ? Number(distanceKm).toFixed(1) : '-'}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">走行距離 (km)</div>
                              </div>
                            </div>
                            {/* 運搬品目タグ＋品目別台数 */}
                            {uniqueItems.length > 0 && (
                              <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-gray-500 flex-shrink-0">運搬品目:</span>
                                {uniqueItems.map((name, i) => (
                                  <span key={i} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">
                                    {name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {/* 品目別台数集計（LOADING_COMPLETEDイベントから集計） */}
                            {(() => {
                              const itemCountMap: Record<string, number> = {};
                              completedLoadings.forEach(e => {
                                itemNamesOf(e).forEach(name => {
                                  itemCountMap[name] = (itemCountMap[name] || 0) + 1;
                                });
                              });
                              const entries = Object.entries(itemCountMap);
                              if (entries.length === 0) return null;
                              return (
                                <div className="mt-2">
                                  <span className="text-xs text-gray-500 block mb-1">品目別台数:</span>
                                  <div className="flex flex-wrap gap-2">
                                    {entries.map(([itemName, count]) => (
                                      <span key={itemName} className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded font-bold">
                                        {itemName}: {count}台
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                            {/* ✅ FB-画像2: 客先別・品目別・経路・回数 集計テーブル */}
                            {(() => {
                              type RouteKey = string;
                              const routeMap = new Map<RouteKey, { customer: string; item: string; route: string; count: number }>();
                              const unloadingEvents = _evs.filter(e => e.eventType === 'UNLOADING_ARRIVED');
                              completedLoadings.forEach(le => {
                                const customer = (le as any).customerName ?? '—';
                                // ✅ 修正②【根本原因】: 選択された品目すべてを「、」区切りで表示する
                                const leItemNames = itemNamesOf(le);
                                const item     = leItemNames.length > 0 ? leItemNames.join('、') : '—';
                                const loadLoc  = le.location?.name ?? '—';
                                const leIdx    = _evs.indexOf(le);
                                const nextUnl  = unloadingEvents.find(ue => _evs.indexOf(ue) > leIdx);
                                const unlLoc   = nextUnl?.location?.name ?? '—';
                                const route    = `${loadLoc}〜${unlLoc}`;
                                const key: RouteKey = `${customer}|${item}|${route}`;
                                const prev = routeMap.get(key);
                                if (prev) { prev.count++; }
                                else { routeMap.set(key, { customer, item, route, count: 1 }); }
                              });
                              const rows = Array.from(routeMap.values());
                              if (rows.length === 0) return null;
                              return (
                                <div className="mt-3">
                                  <span className="text-xs text-gray-500 block mb-1 font-semibold">客先別集計:</span>
                                  <div className="overflow-x-auto rounded border border-gray-200">
                                    <table className="min-w-full text-xs">
                                      <thead>
                                        <tr className="bg-gray-50">
                                          <th className="px-2 py-1 text-left text-gray-500 font-medium border-b border-gray-200">客先</th>
                                          <th className="px-2 py-1 text-left text-gray-500 font-medium border-b border-gray-200">経路</th>
                                          <th className="px-2 py-1 text-left text-gray-500 font-medium border-b border-gray-200">品目</th>
                                          <th className="px-2 py-1 text-center text-gray-500 font-medium border-b border-gray-200">回数</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rows.map((r, i) => (
                                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-2 py-1 text-gray-800 font-medium whitespace-nowrap">{r.customer}</td>
                                            <td className="px-2 py-1 text-gray-600 whitespace-nowrap">{r.route}</td>
                                            <td className="px-2 py-1 text-indigo-700 whitespace-nowrap">{r.item}</td>
                                            <td className="px-2 py-1 text-center font-bold text-blue-700">{r.count}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })()}
                      {/* ─────────────────────────────────────────────
                          タイムラインイベント一覧
                      ───────────────────────────────────────────── */}
                      {(() => {
                        // ─────────────────────────────────────────────
                        // イベントをグループ化:
                        //   LOADING_ARRIVED + LOADING_COMPLETED   → 積込セクション
                        //   UNLOADING_ARRIVED + UNLOADING_COMPLETED → 積降セクション
                        //   それ以外 → 単独イベント（既存表示）
                        // ─────────────────────────────────────────────
                        type BreakEntry = { start: OperationDebugTimelineEvent; end: OperationDebugTimelineEvent | null };
                        type RenderGroup =
                          | { type: 'LOADING_GROUP';   groupNum: number; arrivedEvent: OperationDebugTimelineEvent; completedEvent: OperationDebugTimelineEvent | null; breaks: BreakEntry[]; cargoWork: BreakEntry[] }
                          | { type: 'UNLOADING_GROUP'; groupNum: number; arrivedEvent: OperationDebugTimelineEvent; completedEvent: OperationDebugTimelineEvent | null; breaks: BreakEntry[] }
                          | { type: 'SINGLE'; event: OperationDebugTimelineEvent };
 
                        const groups: RenderGroup[] = [];
                        let loadingGroupNum  = 0;
                        let unloadingGroupNum = 0;
                        const evs = operationDebugTimelineEvents;
 
                        // ✅ FIX-GROUPING: detailIdプレフィックスでペアリング
                        // {uuid}-arrived と {uuid}-completed を同一グループにマージ
                        // 直後イベント前提のロジックから、IDマッチング方式に変更
                        const getDetailId = (id: string) =>
                          id.replace(/-arrived$/, '').replace(/-completed$/, '');
 
                        // BREAK_START + BREAK_END ペアも事前にマップ化
                        const breakEndMap = new Map<string, OperationDebugTimelineEvent>();
                        for (const ev of evs) {
                          if (ev.eventType === 'BREAK_END') {
                            breakEndMap.set(ev.id, ev);
                          }
                        }
                        const usedEvIds = new Set<string>();
 
                        // LOADING_ARRIVED → 同じdetailIdのLOADING_COMPLETEDをマップ化
                        const loadingCompletedMap = new Map<string, OperationDebugTimelineEvent>();
                        const unloadingCompletedMap = new Map<string, OperationDebugTimelineEvent>();
                        for (const ev of evs) {
                          if (ev.eventType === 'LOADING_COMPLETED') {
                            loadingCompletedMap.set(getDetailId(ev.id), ev);
                          } else if (ev.eventType === 'UNLOADING_COMPLETED') {
                            unloadingCompletedMap.set(getDetailId(ev.id), ev);
                          }
                        }

                        // ✅ BUG修正: 「積込完了(単独)→到着→積込完了(グループ内)」二重表示対策
                        // ARRIVED/COMPLETEDの対応関係は、配列 evs の走査順序（=バックエンドの
                        // タイムスタンプソート結果）に依存せず、ここで事前に全て解決しておく。
                        // 同一時刻・僅かな逆転などでCOMPLETEDがARRIVEDより先に配列へ現れても、
                        // 対応するARRIVEDが存在するCOMPLETEDは最初から usedEvIds に含めることで、
                        // 後続のメインループが孤立イベントとして単独カード化してしまうのを防ぐ。
                        for (const ev of evs) {
                          if (ev.eventType === 'LOADING_ARRIVED') {
                            const c = loadingCompletedMap.get(getDetailId(ev.id));
                            if (c) usedEvIds.add(c.id);
                          } else if (ev.eventType === 'UNLOADING_ARRIVED') {
                            const c = unloadingCompletedMap.get(getDetailId(ev.id));
                            if (c) usedEvIds.add(c.id);
                          }
                        }
 
                        // BREAK_START の次のBREAK_ENDをsequence順で探す
                        const breakStartIds = evs
                          .filter(ev => ev.eventType === 'BREAK_START')
                          .map(ev => ev.id);
                        const breakEndList = evs
                          .filter(ev => ev.eventType === 'BREAK_END')
                          .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
                        const breakPairMap = new Map<string, OperationDebugTimelineEvent>();
                        let bEndIdx = 0;
                        for (const bStartId of breakStartIds) {
                          if (bEndIdx < breakEndList.length) {
                            const bEnd = breakEndList[bEndIdx];
                            if (bEnd) {
                              breakPairMap.set(bStartId, bEnd);
                              usedEvIds.add(bEnd.id);
                            }
                            bEndIdx++;
                          }
                        }

                        // ✅ 追加: CARGO_WORK_START + CARGO_WORK_END ペアも同様にマップ化
                        const cargoWorkStartIds = evs
                          .filter(ev => ev.eventType === 'CARGO_WORK_START')
                          .map(ev => ev.id);
                        const cargoWorkEndList = evs
                          .filter(ev => ev.eventType === 'CARGO_WORK_END')
                          .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
                        const cargoWorkPairMap = new Map<string, OperationDebugTimelineEvent>();
                        let cwEndIdx = 0;
                        for (const cwStartId of cargoWorkStartIds) {
                          if (cwEndIdx < cargoWorkEndList.length) {
                            const cwEnd = cargoWorkEndList[cwEndIdx];
                            if (cwEnd) {
                              cargoWorkPairMap.set(cwStartId, cwEnd);
                              usedEvIds.add(cwEnd.id);
                            }
                            cwEndIdx++;
                          }
                        }

                        // ✅ 修正: 荷役開始は「積込到着」より前に押される運用のため、
                        //    進行中ウィンドウでのマッチではなく「その後最初に来る積込」に紐付ける
                        const loadingArrivedSorted = evs
                          .filter(ev => ev.eventType === 'LOADING_ARRIVED')
                          .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
                        const cargoWorkToLoadingMap = new Map<string, string>();
                        for (const cwStartId of cargoWorkStartIds) {
                          const cwStartEv = evs.find(ev => ev.id === cwStartId);
                          if (!cwStartEv) continue;
                          const nextLoading = loadingArrivedSorted.find(le => le.sequenceNumber > cwStartEv.sequenceNumber);
                          if (nextLoading) cargoWorkToLoadingMap.set(cwStartId, nextLoading.id);
                        }
                        const cargoWorkByLoadingId = new Map<string, BreakEntry[]>();

                        // ✅ 積込/荷降の「進行中ウィンドウ」を事前に洗い出す
                        //    （休憩がどの積込/荷降の最中に取得されたかを、evsの並び順に依存せず
                        //      時刻(timestamp)で判定するため。イテレーション順とは独立に動作する）
                        interface HostWindow { arrivedId: string; startMs: number; endMs: number | null; breaks: BreakEntry[]; cargoWork: BreakEntry[] }
                        const hostWindows: HostWindow[] = [];
                        for (const ev of evs) {
                          if (ev.eventType === 'LOADING_ARRIVED' || ev.eventType === 'UNLOADING_ARRIVED') {
                            const detailId = getDetailId(ev.id);
                            const completed = ev.eventType === 'LOADING_ARRIVED'
                              ? loadingCompletedMap.get(detailId)
                              : unloadingCompletedMap.get(detailId);
                            const startMs = ev.timestamp ? new Date(ev.timestamp).getTime() : NaN;
                            if (!Number.isNaN(startMs)) {
                              const endMs = completed?.timestamp ? new Date(completed.timestamp).getTime() : null;
                              hostWindows.push({ arrivedId: ev.id, startMs, endMs, breaks: [], cargoWork: [] });
                            }
                          }
                        }
                        const findHostWindow = (tsMs: number) =>
                          hostWindows.find(w => w.startMs <= tsMs && (w.endMs == null || w.endMs >= tsMs));
 
                        for (const ev of evs) {
                          if (usedEvIds.has(ev.id)) continue;
                          if (ev.eventType === 'LOADING_ARRIVED') {
                            loadingGroupNum++;
                            const detailId = getDetailId(ev.id);
                            const completed = loadingCompletedMap.get(detailId) ?? null;
                            if (completed) usedEvIds.add(completed.id);
                            const hw = hostWindows.find(w => w.arrivedId === ev.id);
                            groups.push({ type: 'LOADING_GROUP', groupNum: loadingGroupNum, arrivedEvent: ev, completedEvent: completed, breaks: hw ? hw.breaks : [], cargoWork: cargoWorkByLoadingId.get(ev.id) ?? [] });
                          } else if (ev.eventType === 'UNLOADING_ARRIVED') {
                            unloadingGroupNum++;
                            const detailId = getDetailId(ev.id);
                            const completed = unloadingCompletedMap.get(detailId) ?? null;
                            if (completed) usedEvIds.add(completed.id);
                            const hw = hostWindows.find(w => w.arrivedId === ev.id);
                            groups.push({ type: 'UNLOADING_GROUP', groupNum: unloadingGroupNum, arrivedEvent: ev, completedEvent: completed, breaks: hw ? hw.breaks : [] });
                          } else if (ev.eventType === 'LOADING_COMPLETED' || ev.eventType === 'UNLOADING_COMPLETED') {
                            // 既に対応ARRIVEDと組んでいれば usedEvIds に入っているのでスキップ済み
                            // 孤立COMPLETEDは単独表示
                            groups.push({ type: 'SINGLE', event: ev });
                          } else if (ev.eventType === 'BREAK_START') {
                            // ✅ 修正②: BREAK_START + BREAK_END を1くくりにする際、
                            // 以前は表示用の合成文字列（「休憩開始 → 10:52 終了」）でnotesを
                            // 上書きしており、それがそのまま編集画面の備考欄に漏れていた。
                            // 生のnotesはそのまま保持し、終了時刻情報は pairedEndId/pairedEndTimestamp
                            // という別フィールドに持たせる（mobileのpairId方式と同じ考え方）。
                            const pairedEnd = breakPairMap.get(ev.id) ?? null;
                            const mergedBreak: OperationDebugTimelineEvent = {
                              ...ev,
                              eventType: pairedEnd ? ('BREAK' as const) : ('BREAK_START' as const),
                              notes: ev.notes ?? null,
                              pairedEndId: pairedEnd?.id ?? null,
                              pairedEndTimestamp: pairedEnd?.timestamp ?? null,
                            };
                            // ✅ 積込/荷降作業中に取得した休憩は、その積込/荷降グループへネストする
                            const breakStartMs = ev.timestamp ? new Date(ev.timestamp).getTime() : NaN;
                            const host = !Number.isNaN(breakStartMs) ? findHostWindow(breakStartMs) : undefined;
                            if (host) {
                              host.breaks.push({ start: mergedBreak, end: pairedEnd });
                            } else {
                              groups.push({ type: 'SINGLE', event: mergedBreak });
                            }
                          } else if (ev.eventType === 'BREAK_END') {
                            // 対応BREAK_STARTとペア済みなら usedEvIds で除外済み → ここには孤立のみ来る
                            groups.push({ type: 'SINGLE', event: ev });
                          } else if (ev.eventType === 'CARGO_WORK_START') {
                            // ✅ 修正: 荷役開始は積込到着より前に押される運用のため、
                            //    「次に来る積込」に紐付けてネストする
                            const pairedCwEnd = cargoWorkPairMap.get(ev.id) ?? null;
                            const mergedCargoWork: OperationDebugTimelineEvent = {
                              ...ev,
                              notes: ev.notes ?? null,
                              pairedEndId: pairedCwEnd?.id ?? null,
                              pairedEndTimestamp: pairedCwEnd?.timestamp ?? null,
                            };
                            const targetLoadingId = cargoWorkToLoadingMap.get(ev.id);
                            if (targetLoadingId) {
                              const arr = cargoWorkByLoadingId.get(targetLoadingId) ?? [];
                              arr.push({ start: mergedCargoWork, end: pairedCwEnd });
                              cargoWorkByLoadingId.set(targetLoadingId, arr);
                            } else {
                              groups.push({ type: 'SINGLE', event: mergedCargoWork });
                            }
                          } else if (ev.eventType === 'CARGO_WORK_END') {
                            // 対応CARGO_WORK_STARTとペア済みなら usedEvIds で除外済み → ここには孤立のみ来る
                            groups.push({ type: 'SINGLE', event: ev });
                          } else {
                            groups.push({ type: 'SINGLE', event: ev });
                          }
                        }
 
                        // ─────────────────────────────────────────────
                        // タイムスタンプ整形ユーティリティ
                        // ─────────────────────────────────────────────
                        const fmtTs = (ts: string | null) =>
                          ts ? new Date(ts).toLocaleString('ja-JP', {
                            timeZone: 'Asia/Tokyo',
                            month: '2-digit', day: '2-digit',
                            hour: '2-digit', minute: '2-digit', second: '2-digit'
                          }) : '-';
                        // ✅ 修正④: mobileと同じ HH:MM 表記（秒なし）のフォーマッタ
                        const fmtHM = (ts: string | null) =>
                          ts ? new Date(ts).toLocaleTimeString('ja-JP', {
                            timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit'
                          }) : '-';
 
                        // ─────────────────────────────────────────────
                        // サブイベント1行のレンダリングヘルパー
                        // isArrived=true の場合: 場所・GPS・時刻のみ表示（コンテンツ非表示）
                        // isArrived=false の場合: items・重量・手書き備考を表示
                        // ─────────────────────────────────────────────
                        // 自動生成されたデフォルト備考 → 表示しない
                        const AUTO_NOTES = ['積込完了', '荷降完了'];

                        const renderSubRow = (
                          subEvent: OperationDebugTimelineEvent,
                          labelText: string,
                          dotColor: string,
                          isFirst: boolean,
                          isArrived: boolean = false,
                          editBtn?: React.ReactNode,
                          timeBadgeLabel: string = '記録時刻',
                          extraBadge?: React.ReactNode
                        ) => (
                          <div className={`px-4 py-3 flex items-start gap-3 ${isFirst ? '' : 'border-t border-gray-100'}`}>
                            <div className={`w-2.5 h-2.5 rounded-full ${dotColor} mt-1.5 flex-shrink-0`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-gray-800">▶ {labelText}</span>
                                {subEvent.timestamp && (
                                  <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                                    {timeBadgeLabel}: {fmtTs(subEvent.timestamp)}
                                  </span>
                                )}
                                {extraBadge}
                                {editBtn && <span className="ml-auto">{editBtn}</span>}
                              </div>
                              {/* ✅ 修正③④: 場所名・住所は到着行のみ表示（完了行は到着情報と重複するため非表示） */}
                              {isArrived && subEvent.location && (
                                <div className="flex items-start gap-1 mt-1 text-xs text-gray-600">
                                  <MapPin className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                                  <span className="break-all">{subEvent.location.name} {subEvent.location.address}</span>
                                </div>
                              )}
                              {subEvent.gpsLocation && (
                                <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-400">
                                  <Navigation className="w-3 h-3 flex-shrink-0" />
                                  <span>
                                    GPS座標: {subEvent.gpsLocation.latitude.toFixed(6)}, {subEvent.gpsLocation.longitude.toFixed(6)}
                                  </span>
                                </div>
                              )}
                              {/* ✅ 修正①③: 品目 - 完了イベントのみ表示。「品目: A、B、C」の1行形式にまとめる */}
                              {!isArrived && ((subEvent as any).detailItems?.length > 0 || subEvent.items || (subEvent as any).customItemName) && (
                                <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-600">
                                  <Package className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  <span>
                                    品目: {[
                                      ...((subEvent as any).detailItems?.length > 0
                                        ? (subEvent as any).detailItems.map((di: any) => `${di.itemName}${di.quantityTons ? ` ${di.quantityTons}t` : ''}`)
                                        : (subEvent.items ? [`${subEvent.items.name}${subEvent.items.unit ? ` (${subEvent.items.unit})` : ''}`] : [])),
                                      ...((subEvent as any).customItemName ? [`${(subEvent as any).customItemName}（手入力）`] : []),
                                    ].join('、')}
                                  </span>
                                </div>
                              )}
                              {/* ⚖️ 重量 - 完了イベントのみ表示 */}
                              {!isArrived && subEvent.quantityTons !== undefined && subEvent.quantityTons > 0 && (
                                <div className="flex items-center gap-1 mt-1">
                                  <span className="inline-flex items-center text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded">
                                    ⚖️ 重量: {Number(subEvent.quantityTons).toFixed(1)} t
                                  </span>
                                </div>
                              )}
                              {/* 備考（手書き内容）- 完了イベントのみ、自動生成テキストは非表示 */}
                              {!isArrived && subEvent.notes && !AUTO_NOTES.includes(subEvent.notes) && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  備考: {subEvent.notes}
                                </div>
                              )}
                              {/* REQ-020: 積載物写真サムネ（完了イベントのみ） */}
                              {!isArrived && (subEvent as any).imageUrl && (() => {
                                const imgUrl = (subEvent as any).imageUrl as string;
                                // Viteプロキシ(/uploads → backend:8443)経由で配信
                                // 自己署名証明書問題を回避するため相対パスをそのまま使用
                                const fullUrl = imgUrl.startsWith('http') ? imgUrl : imgUrl;
                                return (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1">📷 積載物写真</p>
                                    <img
                                      src={fullUrl}
                                      alt="積載物写真"
                                      className="rounded-lg border border-gray-200 cursor-zoom-in hover:opacity-90 transition-opacity shadow-sm"
                                      style={{ maxWidth: '180px', maxHeight: '130px', objectFit: 'cover', display: 'block' }}
                                      onClick={() => {
                                        const modal = document.createElement('div');
                                        modal.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;cursor:zoom-out';
                                        const img = document.createElement('img');
                                        img.src = fullUrl;
                                        img.style.cssText = 'max-width:90vw;max-height:90vh;object-fit:contain;border-radius:8px;box-shadow:0 4px 32px rgba(0,0,0,0.5)';
                                        img.alt = '積載物写真（拡大）';
                                        modal.appendChild(img);
                                        modal.onclick = () => document.body.removeChild(modal);
                                        document.body.appendChild(modal);
                                      }}
                                    />
                                    <p className="text-xs text-gray-400 mt-1">クリックで拡大</p>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
 
                        // ✅ 挿入位置より前の「直前イベントの終了時刻」と「直近の積込イベントの客先」を取得
                        const getInsertDefaults = (afterSeq: number) => {
                          const evsSorted = [...evs].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
                          let prevEndTime: string | null = null;
                          let lastLoadingCustomerId: string | undefined;
                          let lastLoadingCustomerName: string | undefined;
                          for (const a of evsSorted) {
                            if (a.sequenceNumber > afterSeq) break;
                            if (a.timestamp) prevEndTime = a.timestamp;
                            if (a.eventType === 'LOADING' || a.eventType === 'LOADING_ARRIVED' || a.eventType === 'LOADING_COMPLETED') {
                              const cid = (a as any).customerId;
                              const cname = (a as any).customerName;
                              if (cid) { lastLoadingCustomerId = cid; lastLoadingCustomerName = cname; }
                            }
                          }
                          return { afterSeq, prevEndTime, lastLoadingCustomerId, lastLoadingCustomerName };
                        };
                        const renderInsertMarker = (afterSeq: number, mkey: string) => (
                          <div key={mkey} onClick={() => setPickerContext(getInsertDefaults(afterSeq))}
                            className="flex justify-center py-1 cursor-pointer">
                            <div style={{
                              width: 26, height: 26, borderRadius: '50%', background: '#fff',
                              border: '2px dashed #60a5fa', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#2563eb', fontSize: 15, fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            }}>
                              ＋
                            </div>
                          </div>
                        );
                        const maxSeqOfGroup = (group: RenderGroup): number => {
                          if (group.type === 'SINGLE') return group.event.sequenceNumber;
                          return Math.max(group.arrivedEvent.sequenceNumber, group.completedEvent?.sequenceNumber ?? 0);
                        };

                        // ─────────────────────────────────────────────
                        // グループ描画
                        // ─────────────────────────────────────────────
                        return (
                          <>
                            {insertMode && renderInsertMarker(0, 'ins-top')}
                            {groups.map((group, gIdx) => {
                              const groupNode = (() => {
                          if (group.type === 'LOADING_GROUP' || group.type === 'UNLOADING_GROUP') {
                            const isLoading     = group.type === 'LOADING_GROUP';
                            const groupLabel    = isLoading ? '積込' : '荷降';
                            // ✅ トレードカラー適用: 積込=#2196F3(blue) / 荷降=#4CAF50(green)
                            const borderCls     = isLoading ? 'border-blue-400'  : 'border-green-500';
                            const headerBg      = isLoading ? 'bg-blue-50 border-blue-200'   : 'bg-green-50 border-green-200';
                            const headerTextCls = isLoading ? 'text-blue-800'   : 'text-green-800';
                            const arrivedDot    = isLoading ? 'bg-blue-400'     : 'bg-orange-400';
                            const completedDot  = 'bg-green-400';
                            // ✅ 積込・荷降統合編集: 到着・完了どちらの「編集」を押しても
                            // 同じ画面（両方の時刻・場所・客先・品目をまとめて編集）を開く
                            const openGroupEdit = () => setEditEvent({
                              id: group.arrivedEvent.id,
                              realDetailId: group.arrivedEvent.id.replace(/-arrived$|-completed$/, ''),
                              eventType: isLoading ? 'LOADING' : 'UNLOADING',
                              timestamp: group.arrivedEvent.timestamp,
                              completionTimestamp: group.completedEvent?.timestamp ?? null,
                              notes: group.completedEvent?.notes ?? group.arrivedEvent.notes,
                              quantityTons: group.completedEvent?.quantityTons,
                              locationName: group.arrivedEvent.location?.name ?? '',
                              locationId: group.arrivedEvent.location?.id ?? '',
                              locationLat: group.arrivedEvent.gpsLocation?.latitude ?? group.arrivedEvent.location?.latitude ?? null,
                              locationLng: group.arrivedEvent.gpsLocation?.longitude ?? group.arrivedEvent.location?.longitude ?? null,
                              detailItems: (group.completedEvent as any)?.detailItems ?? null,
                              itemId: group.completedEvent?.items?.id ?? null,
                              itemName: group.completedEvent?.items?.name ?? null,
                              customerId: (group.arrivedEvent as any).customerId ?? (group.completedEvent as any)?.customerId ?? null,
                              customerName: (group.arrivedEvent as any).customerName ?? (group.completedEvent as any)?.customerName ?? null,
                            });
 
                            return (
                              <div key={`group-${gIdx}`} className={`border-2 ${borderCls} rounded-lg overflow-hidden`}>
                                {/* グループヘッダー */}
                                <div className={`${headerBg} border-b px-4 py-2 flex items-center gap-2 flex-wrap`}>
                                  <Truck className={`w-4 h-4 ${headerTextCls} flex-shrink-0`} />
                                  <span className={`text-sm font-bold ${headerTextCls}`}>
                                    {groupLabel}
                                    {group.groupNum > 1 && `（${group.groupNum}回目）`}
                                  </span>
                                  {/* 🆕 荷役作業トグルの表示（積込グループのみ） */}
                                  {isLoading && ((group.completedEvent as any)?.hasCargoWork || (group.arrivedEvent as any).hasCargoWork) && (
                                    <span className="text-xs font-bold text-white bg-orange-500 px-2 py-1 rounded-full flex-shrink-0 shadow-sm">
                                      🔧 荷役あり
                                    </span>
                                  )}
                                  {group.arrivedEvent.location && (
                                    <span className="text-xs text-gray-500 truncate">
                                      ─ {group.arrivedEvent.location.name}
                                    </span>
                                  )}
                                  {((group.arrivedEvent as any).customerName || (group.completedEvent as any)?.customerName) && (
                                    <span className="text-xs font-semibold truncate" style={{ color: isLoading ? '#1565C0' : '#2E7D32' }}>
                                      🏢 {(group.arrivedEvent as any).customerName || (group.completedEvent as any)?.customerName}
                                    </span>
                                  )}
                                  {/* ヘッダー編集ボタンは廃止 → 各サブ行に個別ボタンを配置 */}
                                </div>
                                {/* サブイベント */}
                                <div className="bg-white">
                                  {renderSubRow(group.arrivedEvent, '到着', arrivedDot, true, true,
                                    <button type="button"
                                      onClick={openGroupEdit}
                                      className="flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                    ><Pencil className="w-3 h-3" /> 編集</button>
                                  )}
                                  {/* ✅ 積込/荷降作業中に取得した休憩をグループ内にネスト表示 */}
                                  {group.breaks.map((brk, bi) => (
                                    <React.Fragment key={brk.start.id || `brk-${bi}`}>
                                      {renderSubRow(
                                        brk.start,
                                        '休憩・待機',
                                        'bg-purple-500',
                                        false,
                                        false,
                                        <button type="button"
                                          onClick={() => setEditEvent({
                                            id: brk.start.id,
                                            realDetailId: brk.start.id.replace(/-arrived$|-completed$/, ''),
                                            eventType: brk.start.pairedEndId ? 'BREAK' : 'BREAK_START',
                                            timestamp: brk.start.timestamp,
                                            completionTimestamp: brk.start.pairedEndTimestamp ?? null,
                                            pairedEndId: brk.start.pairedEndId ?? null,
                                            notes: brk.start.notes,
                                          })}
                                          className="flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                        ><Pencil className="w-3 h-3" /> 編集</button>,
                                        '開始時刻',
                                        brk.start.pairedEndTimestamp ? (
                                          <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                                            終了時刻: {fmtHM(brk.start.pairedEndTimestamp)}
                                          </span>
                                        ) : undefined
                                      )}
                                    </React.Fragment>
                                  ))}
                                  {/* ✅ 追加: 積込中に記録した荷役作業をグループ内にネスト表示 */}
                                  {group.type === 'LOADING_GROUP' && group.cargoWork.map((cw, ci) => (
                                    <React.Fragment key={cw.start.id || `cw-${ci}`}>
                                      {renderSubRow(
                                        cw.start,
                                        '荷役',
                                        'bg-indigo-500',
                                        false,
                                        false,
                                        <button type="button"
                                          onClick={() => setEditEvent({
                                            id: cw.start.id,
                                            realDetailId: cw.start.id.replace(/-arrived$|-completed$/, ''),
                                            eventType: cw.start.pairedEndId ? 'CARGO_WORK_END' : 'CARGO_WORK_START',
                                            timestamp: cw.start.timestamp,
                                            completionTimestamp: cw.start.pairedEndTimestamp ?? null,
                                            pairedEndId: cw.start.pairedEndId ?? null,
                                            notes: cw.start.notes,
                                          })}
                                          className="flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                        ><Pencil className="w-3 h-3" /> 編集</button>,
                                        '開始時刻',
                                        cw.start.pairedEndTimestamp ? (
                                          <span className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                                            終了時刻: {fmtHM(cw.start.pairedEndTimestamp)}
                                          </span>
                                        ) : undefined
                                      )}
                                    </React.Fragment>
                                  ))}
                                  {group.completedEvent && renderSubRow(
                                    group.completedEvent,
                                    isLoading ? '積込完了' : '荷降完了',
                                    completedDot,
                                    false,
                                    false,
                                    <button type="button"
                                      onClick={openGroupEdit}
                                      className="flex items-center gap-1 px-2 py-0.5 rounded text-xs border border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                    ><Pencil className="w-3 h-3" /> 編集</button>
                                  )}
                                </div>
                              </div>
                            );
                          }
 
                          // ─────────────────────────────────────────
                          // 通常イベント（TRIP_START/END, 点検, 給油, 休憩等）
                          // ─────────────────────────────────────────
                          const { event } = group;
                          const typeInfo = getEventTypeInfo(event.eventType);
                          return (
                            <div key={event.id} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex items-start gap-3">
                                {/* シーケンス番号 */}
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-sm font-semibold text-blue-600">{event.sequenceNumber}</span>
                                </div>
 
                                <div className="flex-1">
                                  {/* イベント種別と時刻 + 編集ボタン */}
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <span className={`px-3 py-1 text-sm font-semibold rounded-lg inline-flex items-center gap-2 ${typeInfo.className}`}>
                                      {typeInfo.icon}
                                      {typeInfo.label}
                                    </span>
                                    {event.timestamp && (
                                      <span className="text-sm font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">
                                        {isBreakGroupEvt(event.eventType) ? '開始時刻' : '記録時刻'}: {fmtHM(event.timestamp)}
                                      </span>
                                    )}
                                    {/* ✅ 休憩統合編集: 終了時刻バッジ（mobileの「休憩終了」表示と統一） */}
                                    {(event as any).pairedEndTimestamp && (
                                      <span className="text-sm font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">
                                        終了時刻: {fmtHM((event as any).pairedEndTimestamp)}
                                      </span>
                                    )}
                                    {/* ✅ 編集ボタン */}
                                    <button
                                      type="button"
                                      onClick={() => setEditEvent({
                                        id: event.id,
                                        realDetailId: event.id.replace(/-arrived$|-completed$/, ''),
                                        eventType: event.eventType,
                                        timestamp: event.timestamp,
                                        // ✅ 休憩統合編集: ペアの終了時刻・終了レコードIDを引き継ぐ
                                        completionTimestamp: (event as any).pairedEndTimestamp ?? null,
                                        pairedEndId: (event as any).pairedEndId ?? null,
                                        notes: event.notes,
                                        quantityTons: event.quantityTons,
                                        fuelCostYen: (event as any).fuelCostYen ?? null,
                                        detailItems: (event as any).detailItems ?? null,
                                        locationName: event.location?.name ?? '',
                                        locationId: event.location?.id ?? '',
                                        locationLat: event.gpsLocation?.latitude ?? event.location?.latitude ?? null,
                                        locationLng: event.gpsLocation?.longitude ?? event.location?.longitude ?? null,
                                        itemId: event.items?.id ?? null,
                                        itemName: event.items?.name ?? null,
                                        customerId: (event as any).customerId ?? null,
                                        customerName: (event as any).customerName ?? null,
                                        preinspMemo: event.eventType === 'PRE_INSPECTION' ? ((event as any).overallNotes ?? '') : null,
                                        // ✅ 修正①: 運行後点検の点検メモ・走行距離・燃料レベルを編集モーダルへ引き継ぐ
                                        overallNotes: event.eventType === 'POST_INSPECTION' ? ((event as any).overallNotes ?? '') : null,
                                        totalDistanceKm: (event as any).totalDistanceKm ?? null,
                                        fuelConsumedLiters: (event as any).fuelConsumedLiters ?? null,
                                        // ✅ 修正③: 手入力品目名を編集モーダルへ引き継ぐ
                                        customItemName: (event as any).customItemName ?? null,
                                      })}
                                      className="ml-auto flex items-center gap-1 px-2 py-1 border border-gray-300 rounded text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                      title="このイベントを編集"
                                    >
                                      <Pencil className="w-3 h-3" /> 編集
                                    </button>
                                  </div>
 
                                  {/* 場所情報 */}
                                  {event.location && (
                                    <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
                                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                      <div>
                                        <p className="font-medium">{event.location.name}</p>
                                        <p className="text-xs text-gray-500">{event.location.address}</p>
                                      </div>
                                    </div>
                                  )}
 
                                  {/* ✅ 修正④: GPS座標の生数値はユーザー向けタイムラインには不要（mobileも非表示） */}
 
                                  {/* ✅ 修正①③: 品目・数量 - すべての品目（手入力含む）を「品目: A、B、C」の1行にまとめて表示 */}
                                  {((event as any).detailItems?.length > 0 || event.items || (event as any).customItemName) && (
                                    <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
                                      <Package className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                                      <span>
                                        品目: {[
                                          ...((event as any).detailItems?.length > 0
                                            ? (event as any).detailItems.map((di: any) => `${di.itemName}${di.quantityTons ? ` ${di.quantityTons}t` : ''}`)
                                            : (event.items ? [`${event.items.name}${event.quantityTons ? ` ${event.quantityTons}t` : ''}`] : [])),
                                          ...((event as any).customItemName ? [`${(event as any).customItemName}（手入力）`] : []),
                                        ].join('、')}
                                      </span>
                                    </div>
                                  )}
 
                                  {/* 点検詳細 */}
                                  {event.inspectionDetails && (
                                    <div className="bg-gray-50 rounded-lg p-3 mb-2">
                                      <div className="flex items-center gap-4 text-sm">
                                        <span className="text-gray-600">
                                          ステータス:
                                          <span className={`ml-1 font-medium ${
                                            event.inspectionDetails.status === 'COMPLETED' ? 'text-green-600' :
                                            event.inspectionDetails.status === 'IN_PROGRESS' ? 'text-blue-600' :
                                            'text-gray-600'
                                          }`}>
                                            {event.inspectionDetails.status === 'COMPLETED' ? '完了' :
                                             event.inspectionDetails.status === 'IN_PROGRESS' ? '実施中' : '待機中'}
                                          </span>
                                        </span>
                                        <span className="text-gray-600">
                                          合格: <span className="font-medium text-green-600">{event.inspectionDetails.passedItems}</span>
                                          /{event.inspectionDetails.totalItems}項目
                                        </span>
                                        {event.inspectionDetails.failedItems > 0 && (
                                          <span className="text-red-600 font-medium">
                                            不合格: {event.inspectionDetails.failedItems}件
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
 
                                  {/* ✅ 修正①: 運行前点検・運行後点検メモの表示（overallNotesを使用） */}
                                  {['PRE_INSPECTION', 'POST_INSPECTION'].includes(event.eventType) && (event as any).overallNotes && (
                                    <div className="text-sm text-gray-600 mt-2">
                                      <span className="font-medium">点検メモ:</span> {(event as any).overallNotes}
                                    </div>
                                  )}
                                  {/* ✅ 修正①: 運行後点検の走行距離・燃料レベル表示 */}
                                  {event.eventType === 'POST_INSPECTION' && ((event as any).totalDistanceKm || (event as any).fuelConsumedLiters) && (
                                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                                      {(event as any).totalDistanceKm ? <span>走行距離: {(event as any).totalDistanceKm} km</span> : null}
                                      {(event as any).fuelConsumedLiters ? <span>燃料レベル: {(event as any).fuelConsumedLiters} L</span> : null}
                                    </div>
                                  )}
                                  {/* ✅ 修正⑤: 給油量・金額表示 */}
                                  {['FUELING', 'REFUELING'].includes(event.eventType) && (Number(event.quantityTons) > 0 || (event as any).fuelCostYen) && (
                                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                                      {Number(event.quantityTons) > 0 ? <span>給油量: {Number(event.quantityTons).toFixed(1)} L</span> : null}
                                      {(event as any).fuelCostYen ? <span>金額: ¥{Number((event as any).fuelCostYen).toLocaleString()}</span> : null}
                                    </div>
                                  )}
                                  {/* 備考（点検イベント以外） */}
                                  {!['PRE_INSPECTION', 'POST_INSPECTION', 'TRIP_START', 'TRIP_END'].includes(event.eventType) && event.notes && (
                                    <div className="text-sm text-gray-600 mt-2">
                                      <span className="font-medium">備考:</span> {event.notes}
                                    </div>
                                  )}
                                  {/* REQ-020: 積載物写真サムネ + 拡大モーダル */}
                                  {(event as any).imageUrl && (() => {
                                    const imgUrl = (event as any).imageUrl as string;
                                    // stagingのバックエンドURLを考慮（/uploads/ は nginx経由でバックエンドから配信）
                                    // ✅ REQ-020修正: VITE_API_BASE_URL からバックエンドオリジンを正確に取得
                                    // Viteプロキシ(/uploads → backend:8443)経由で配信
                                    // 自己署名証明書問題を回避するため相対パスをそのまま使用
                                    const fullUrl = imgUrl.startsWith('http') ? imgUrl : imgUrl;
                                    return (
                                      <div className="mt-2">
                                        <p className="text-xs text-gray-500 mb-1">📷 積載物写真</p>
                                        <img
                                          src={fullUrl}
                                          alt="積載物写真"
                                          className="rounded-lg border border-gray-200 cursor-zoom-in hover:opacity-90 transition-opacity shadow-sm"
                                          style={{ maxWidth: '180px', maxHeight: '130px', objectFit: 'cover', display: 'block' }}
                                          onClick={() => {
                                            const modal = document.createElement('div');
                                            modal.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;cursor:zoom-out';
                                            const img = document.createElement('img');
                                            img.src = fullUrl;
                                            img.style.cssText = 'max-width:90vw;max-height:90vh;object-fit:contain;border-radius:8px;box-shadow:0 4px 32px rgba(0,0,0,0.5)';
                                            img.alt = '積載物写真（拡大）';
                                            modal.appendChild(img);
                                            modal.onclick = () => document.body.removeChild(modal);
                                            document.body.appendChild(modal);
                                          }}
                                        />
                                        <p className="text-xs text-gray-400 mt-1">クリックで拡大</p>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          );
                              })();
                              return (
                                <React.Fragment key={`grpwrap-${gIdx}`}>
                                  {groupNode}
                                  {insertMode && renderInsertMarker(maxSeqOfGroup(group), `ins-${gIdx}`)}
                                </React.Fragment>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  {operationDebugTimelineEvents.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      タイムラインデータがありません
                    </div>
                  )}
                </div>
              )}

              {/* ✅ GPSルートタブ - Google Maps実装 */}
              {activeTab === 'gps' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Navigation className="w-5 h-5 text-gray-600" />
                    GPSルート ({timelineGpsPoints.length > 0 ? timelineGpsPoints.length : gpsRecords.length}ポイント)
                    {routeGpsLogs.length > 0 && (
                      <span className="text-sm font-normal text-blue-600 ml-2">
                        走行軌跡: 表示{routeGpsLogs.length}点
                        {totalGpsCount > routeGpsLogs.length && (
                          <span className="text-gray-400"> / 記録{totalGpsCount.toLocaleString()}点</span>
                        )}
                      </span>
                    )}
                  </h3>

                  {/* 凡例 */}
                  {(routeGpsLogs.length > 0 || timelineGpsPoints.length > 0) && (
                    <div className="flex items-center gap-4 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                      <span className="font-medium text-gray-700">凡例:</span>
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-6 h-0.5 bg-blue-600 rounded" style={{ height: '3px' }}></span>
                        走行軌跡（GPS実測ログ）
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-6 h-0.5 bg-emerald-500 rounded" style={{ height: '2px' }}></span>
                        イベント接続線
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
                        S: 運行開始
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
                        E: 運行終了
                      </span>
                    </div>
                  )}
                  
                  {/* ✅ 常に地図エリアを表示 */}
                  <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden" style={{ minHeight: '500px' }}>
                    {/* Google Mapsエラー表示 */}
                    {mapError ? (
                      <div className="flex items-center justify-center h-96 bg-red-50">
                        <div className="text-center p-8">
                          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                          <h4 className="text-lg font-semibold text-red-900 mb-2">地図の読み込みエラー</h4>
                          <p className="text-red-700">{mapError}</p>
                        </div>
                      </div>
                    ) : !mapsLoaded ? (
                      <div className="flex items-center justify-center h-96 bg-blue-50">
                        <div className="text-center p-8">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <h4 className="text-lg font-semibold text-blue-900 mb-2">Google Mapsを読み込み中...</h4>
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        {/* ✅ Google Maps コンテナ - 常に表示 */}
                        <div 
                          ref={mapRef}
                          className="w-full h-96"
                          style={{ minHeight: '400px', backgroundColor: '#e5e7eb' }}
                        />
                        
                        {/* ✅ GPS記録なしオーバーレイ */}
                        {timelineGpsPoints.length === 0 && gpsRecords.length === 0 && routeGpsLogs.length === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
                            <div className="text-center p-8">
                              <Navigation className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                              <h4 className="text-lg font-semibold text-gray-700 mb-2">GPS記録がありません</h4>
                              <p className="text-sm text-gray-500">この運行にはGPS記録が存在しません</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 地図情報パネル */}
                    <div className="bg-gray-50 p-4 border-t border-gray-200">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">総走行距離</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {operation?.totalDistanceKm || 0} km
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">GPS記録ポイント</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {timelineGpsPoints.length > 0 ? timelineGpsPoints.length : gpsRecords.length}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">運行時間</p>
                          <p className="text-lg font-semibold text-gray-900">
                            {operation?.actualStartTime && operation?.actualEndTime
                              ? `${Math.round(
                                  (new Date(operation.actualEndTime).getTime() -
                                    new Date(operation.actualStartTime).getTime()) /
                                    (1000 * 60)
                                )} 分`
                              : '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* GPS記録リスト */}
                  {gpsRecords.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold mb-3">GPS記録サマリー</h4>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {gpsRecords.slice(0, 10).map((record, index) => (
                          <div
                            key={record.id}
                            className="flex items-center justify-between bg-white p-3 rounded border border-gray-200"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
                              <div>
                                <p className="text-sm font-medium">
                                  {new Date(record.recordedAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatGps(record.latitude, record.longitude)}
                                </p>
                              </div>
                            </div>
                            {record.speedKmh !== undefined && (
                              <div className="text-sm text-gray-600">{record.speedKmh} km/h</div>
                            )}
                          </div>
                        ))}
                        {gpsRecords.length > 10 && (
                          <p className="text-sm text-gray-500 text-center py-2">
                            他 {gpsRecords.length - 10} 件の記録
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ✅ 点検項目詳細タブ - OperationDebugテーブル表示に完全置き換え */}
              {activeTab === 'inspection' && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5 text-gray-600" />
                      <h2 className="text-lg font-semibold text-gray-900">点検項目 ({inspectionItemDetails.length}件)</h2>
                    </div>
                    <button
                      onClick={() => setShowInspectionDetails(!showInspectionDetails)}
                      className="p-1 text-gray-500 hover:text-gray-700"
                    >
                      {showInspectionDetails ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {showInspectionDetails && inspectionItemDetails.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">点検種別</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">点検項目名</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">カテゴリ</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">結果</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">判定</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">不具合レベル</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">備考</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">点検日時</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {inspectionItemDetails.map((item) => (
                            <tr key={`${item.inspectionRecordId}-${item.inspectionItemId}`} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm">
                                {getInspectionTypeBadge(item.inspectionType)}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {item.inspectionItemName}
                                {item.inspectionItemDescription && (
                                  <p className="text-xs text-gray-500 mt-1">{item.inspectionItemDescription}</p>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                  {item.inspectionItemCategory || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">{item.resultValue || '-'}</td>
                              <td className="px-4 py-3 text-sm">
                                <div className="flex items-center gap-2">
                                  {getPassedIcon(item.isPassed)}
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    item.isPassed === null ? 'bg-gray-100 text-gray-700' :
                                    item.isPassed ? 'bg-green-100 text-green-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {item.isPassed === null ? '未実施' : item.isPassed ? '合格' : '不合格'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">{item.defectLevel || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{item.notes || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{formatTime(item.checkedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {inspectionItemDetails.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      点検項目データがありません
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* フッター - アクションボタン */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <Button variant="outline" onClick={onClose}>
                閉じる
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>

    {/* ✅ イベント編集モーダル */}
    {editEvent && (
      <CmsActivityEditModal
        event={editEvent}
        operationId={operationId}
        items={editItems}
        customers={editCustomers}
        onClose={() => setEditEvent(null)}
        onSaved={() => {
          setEditEvent(null);
          fetchIntegratedTimeline(operationId);
        }}
        onDeleted={(id) => {
          setEditEvent(null);
          setOperationDebugTimelineEvents(prev => prev.filter(e => e.id !== id));
        }}
      />
    )}

    {/* 🆕 イベント追加モーダル */}
    {pickerContext && (
      <CmsActivityAddModal
        operationId={operationId}
        items={editItems}
        customers={editCustomers}
        vehicleId={operation?.vehicleId}
        mapsLoaded={mapsLoaded}
        insertAfterSequenceNumber={pickerContext.afterSeq}
        defaultStartTime={pickerContext.prevEndTime}
        defaultCustomerId={pickerContext.lastLoadingCustomerId}
        defaultCustomerName={pickerContext.lastLoadingCustomerName}
        onClose={() => setPickerContext(null)}
        onSaved={() => {
          setPickerContext(null);
          setInsertMode(false);
          fetchIntegratedTimeline(operationId);
        }}
      />
    )}
    </>
  );
};

export default OperationDetailDialog;